
import { InputClock, MAX_IN_FLIGHT } from "../client/clock";
import { Predictor } from "../client/prediction";
import { guildBuildingCollision, type GuildAttributeTable, type ObjectWorld, type StandingBuilding } from "../sim/collision";
import { HEADING_MASK } from "../sim/fixed";
import {
  RemoteMotion,
  pushOutcome,
  type PushOutcome,
  type DrawnMotion,
  type MotionSample,
} from "../render/remotemotion";
import {
  DEFAULT_STATS,
  Gait,
  type MoveState,
  type MoveStats,
  TICK_MS,
  moveState,
} from "../sim/movement";
import { chatTails } from "../render/chattails";
import { emoticons } from "../render/emoticons";
import { Reader } from "./codec";
import {
  Attack,
  Bag as BagFrame,
  type BagSlot,
  Gold as GoldFrame,
  ItemDel as ItemDelFrame,
  ItemSet as ItemSetFrame,
  BuyItem,
  Chat,
  ChatEcho,
  Guild as GuildFrame,
  GuildName as GuildNameFrame,
  GuildLandState as GuildLandStateFrame,
  LandList as LandListFrame,
  GuildAnswer,
  GuildBank,
  GuildCreate,
  GuildDeleteComment,
  GuildRefreshComments,
  GuildPostComment,
  GuildBuyLand,
  GuildDisband,
  GuildExpel,
  GuildGeneral,
  GuildFoundingPrompt,
  GuildHandOver,
  GuildInvitation as GuildInvitationFrame,
  GuildInvite,
  GuildLeave,
    GuildOffer,
    GuildChargePower,
    GuildSkillUp,
    GuildUseSkill,
    GuildPlaceBuilding,
    GuildUpgradeBuilding,
  GuildPromote,
  GuildRank,
  GuildWarChallenge,
  GuildWarRefuse,
  GuildWarMenu as GuildWarMenuFrame,
  GuildWarArena as GuildWarArenaFrame,
  GuildComments as GuildCommentsFrame,
  CloseShop,
  CombatEvent,
  SyncPosition,
  CreateFly,
  SpecialEffect as SpecialEffectFrame,
  SpecificEffect as SpecificEffectFrame,
  PointChange as PointChangeFrame,
  type EntityDelta,
  Hello,
  Input,
  Join,
  Arrive,
  MapChange as MapChangeFrame,
  Opcode,
  PROTOCOL_VERSION,
  Ping,
  Pong,
  Reject,
  Characters as CharactersFrame,
  EmpireSelectPrompt,
  SelectEmpire,
  SelectCharacter,
  DeleteCharacter,
  Register,
  Registered,
  CreateCharacter,
  SCHEMA_HASH,
  Login,
  EquipItem,
  Notice as NoticeFrame,
  PickUp,
  PvpDuel,
  PvpStatus,
  QuestDialog as QuestDialogFrame,
  QuestInputAnswer,
  QuestInputPrompt as QuestInputPromptFrame,
  QuestConfirmAnswer,
  QuestConfirmPrompt as QuestConfirmPromptFrame,
  QuestItemAnswer,
  QuestItemPrompt as QuestItemPromptFrame,
  QuestInfo,
  QuestLog as QuestLogFrame,
  NpcPositions,
  QuestTargetCreate,
  QuestTargetDelete,
  QuestTargetUpdate,
  QuestReply,
  CubeInfo,
  CubeMake,
  CubeRecipes,
  CubeResult,
  CubeClosed,
  DragonSoulDeck,
  DragonSoulMove,
  DragonSoulRefine,
  DragonSoulRefined,
  DragonSouls,
  Refine,
  RefineInfo,
  RefineOffer,
  RefineResult,
  ExchangeStart,
  ExchangeOffer,
  ExchangeWithdraw,
  ExchangeGold,
  ExchangeAccept,
  ExchangeCancel,
  ExchangeState,
  Emote,
  EmoteAnswer,
  EmotePlayed as EmotePlayedFrame,
  EmoteRequest as EmoteRequestFrame,
  Fish,
  FishCaught as FishCaughtFrame,
  Fishing as FishingFrame,
  FishingAct as FishingActFrame,
  Mine,
  MineResult as MineResultFrame,
  OXAnswer,
  OXQuestion as OXQuestionFrame,
  OXResult as OXResultFrame,
  AutoBanQuiz as AutoBanQuizFrame,
  DungeonState as DungeonStateFrame,
  Mining as MiningFrame,
  Recall,
  RestartHere,
  UseDungeonDoor,
  Warp as WarpFrame,
  Mount as MountFrame,
  WalkMode as WalkModeFrame,
  // ---- polymorph and pets
  PetCommand,
  PetSummoned as PetSummonedFrame,
  Polymorphed as PolymorphedFrame,
  // ---- arena
  ArenaAnswer,
  ArenaChallenge,
  ArenaWatch,
  ArenaDuel as ArenaDuelFrame,
  ArenaInvitation as ArenaInvitationFrame,
  ArenaResult as ArenaResultFrame,
  // ---- a player's own stall
  OpenStall,
  CloseStall,
  EnterStall,
  LeaveStall,
  BuyFromStall,
  StallListing,
  StallSign as StallSignFrame,
  StallSold as StallSoldFrame,
  StallWindow as StallWindowFrame,
  // ---- target
  Target,
  FlyTarget,
  Aimed as AimedFrame,
  Targeted as TargetedFrame,
  EquipmentView as EquipmentViewFrame,
  // ---- marriage and the friend list
  ProposeMarriage,
  AnswerMarriage,
  MarriageCommand,
  AddFriend,
  AnswerFriend,
  RemoveFriend,
  SetBlockMode,
  MarriageStatus as MarriageStatusFrame,
  FriendList as FriendListFrame,
  MonarchInfo as MonarchInfoFrame,
  // ---- the auction house and the item mall
  OpenAuction,
  ListLot,
  PlaceBid,
  BuyLotNow,
  ClaimLot,
  MallCheckout,
  AuctionBoard as AuctionBoardFrame,
  AuctionNews as AuctionNewsFrame,
  MallWindow as MallWindowFrame,
  OpenSafebox,
  Party as PartyFrame,
  PartyAnswer,
  PartyAttacker,
  PartyRole,
  PartyUseSkill,
  PartyExpMode,
  PartyInvitation as PartyInvitationFrame,
  PartyInvite,
  PartyKick,
  PartyLeave,
  Whisper,
  Whispered,
  SafeboxUnlock,
  SafeboxDeposit,
  SafeboxWithdraw,
  SafeboxMove,
  CloseSafebox,
  OpenGoldBarShop,
  Safebox,
  Shop as ShopFrame,
  ClickNpc,
  SellItem,
  SetPvpMode,
  SkillUp,
  SpendPoint,
  StatusReset,
  StatusResetConfirm,
  StatusResetOffer,
  Skills as SkillsFrame,
  QuickSlotAdd,
  QuickSlotDel,
  QuickSlotSwap,
  QuickSlots as QuickSlotsFrame,
  Affects as AffectsFrame,
  Spirit as SpiritFrame,
  Stamina as StaminaFrame,
  Snapshot,
  Standing,
  UseSkill,
  Unequip,
  UseItem,
  DropItem,
  DropGold,
  MoveItem,
  UseItemToItem,
  UseItemToNpc,
  Welcome,
  Environment,
  Weather,
  FieldMusic,
  // ---- guild emblems
  Emblem as EmblemFrame,
  GuildSymbol as GuildSymbolFrame,
  GuildSymbolChunk,
  GuildSymbolCRC,
  RequestEmblem,
  UploadEmblem,
} from "./messages";
import { NO_STAMINA, type Stamina_ } from "./stamina";
import {
  SwingLock,
  cooldownTicksFor,
  type CombatResolution,
  type FlyLaunch,
} from "../client/combat";
import { NO_ITEM_FACTS, itemFactsOf, type ItemFacts, type ItemStats } from "../itemfacts";

/** Re-exported because eleven panel tests build a fixture with it and this is
 *  the module they already import from. The declaration is in `itemfacts.ts`. */
export { NO_ITEM_FACTS };

export const PING_INTERVAL_MS = 2000;

// The party heal's skill index (`packet.h:1590-1593`, `uiparty.py:44`).
const PARTY_SKILL_HEAL = 1;

const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 15_000;
/** Reconnect delay grows geometrically and is jittered. Without jitter, every
 *  client disconnected by one server restart returns in the same millisecond. */

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "handshaking"
  | "joined"
  | "rejected"
  | "offline";

/** Where the input frames come from. Returned values are validated, not trusted. */
export interface InputSource {
  sample(): { heading: number; gait: Gait };
}

/** `EntityDelta.kind`. A monster is not a player with fields left blank. */
export const EntityKind = {
  PLAYER: 0,
  MONSTER: 1,
  NPC: 2,
  /** A metin stone: it does not move and does not swing, and it is drawn and
   *  targeted as its own thing rather than inferred from its statistics. */
  STONE: 3,
  /** Something lying on the ground. Its `itemKind` says how to draw it. */
  ITEM: 4,
  /** A durable guild-land building, drawn as a world marker rather than an NPC. */
  BUILDING: 5,
} as const;

export type EntityKind = (typeof EntityKind)[keyof typeof EntityKind];

/** `EntityDelta.flags`, bit 0. Mirrors `metin3/net/schema.py`'s
 *  `ENTITY_SHOPKEEPER`. Set only on the four town dealers -- a client has no
 *  other way to tell a weapon dealer from a guard, since both are NPCs with no
 *  health and no AI. */
export const ENTITY_SHOPKEEPER = 1 << 0;

export const ENTITY_BLACKSMITH = 1 << 1;

/** `EntityDelta.flags`, bit 2. Mirrors `ENTITY_KEEPER`: the Storekeeper, who
 *  keeps the account's storeroom. Its own bit rather than a second meaning for
 *  the dealer flag, which he does not carry -- his shop is opened from a quest
 *  script this build does not load. */
export const ENTITY_KEEPER = 1 << 2;

/** Whether this entity will buy what the player is carrying. */
export function isShopkeeper(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_SHOPKEEPER) !== 0;
}

/** Whether this entity will try to improve what the player is carrying. */
export function isBlacksmith(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_BLACKSMITH) !== 0;
}

export const ENTITY_CUBE = 1 << 5;

/** `EntityDelta.flags`, bit 6. Mirrors `ENTITY_ALCHEMIST`: the Alchemist,
 *  20001, the only NPC any `ds.*` Lua function is ever reached through. He
 *  stands in the a1 plaza among the guards, sells nothing and carries no other
 *  flag, so there is nothing about him a client could infer. */
export const ENTITY_ALCHEMIST = 1 << 6;

/** Whether this entity works dragon souls. */
export function isAlchemist(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_ALCHEMIST) !== 0;
}

/** Whether this entity will build things out of what the player is carrying. */
export function isCubeCounter(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_CUBE) !== 0;
}

export const ENTITY_ORE_VEIN = 1 << 7;

export const ENTITY_MINING = 1 << 8;

/** Whether there is ore in this entity. */
export function isOreVein(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_ORE_VEIN) !== 0;
}

/** Whether this entity is swinging a pick. */
export function isMining(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_MINING) !== 0;
}

export const ENTITY_DUNGEON_DOOR = 1 << 11;

/** Whether this entity is a door, which `CInstanceBase::RenderToShadowMap` never casts (`InstanceBase.cpp:2006-2007`). */
export function isDungeonDoor(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_DUNGEON_DOOR) !== 0;
}

/** `EntityDelta.flags`, bit 12. Mirrors `ENTITY_KILLER`: the server marked
 * this player after a permitted PK attack and clears it itself after thirty
 * seconds. This is display-only; browser code never infers permission from it. */
export const ENTITY_KILLER = 1 << 12;

export function isKiller(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_KILLER) !== 0;
}

export const ENTITY_CHAOTIC = 1 << 15;

export function isChaotic(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_CHAOTIC) !== 0;
}

const ENTITY_ALIGNMENT_LAWFUL_1 = 1 << 16;
const ENTITY_ALIGNMENT_LAWFUL_2 = 1 << 17;
const ENTITY_ALIGNMENT_LAWFUL_3 = 1 << 18;
const ENTITY_ALIGNMENT_LAWFUL_4 = 1 << 19;
const ENTITY_ALIGNMENT_CHAOTIC_1 = 1 << 20;
const ENTITY_ALIGNMENT_CHAOTIC_2 = 1 << 21;
const ENTITY_ALIGNMENT_CHAOTIC_3 = 1 << 22;
const ENTITY_ALIGNMENT_CHAOTIC_4 = 1 << 23;

const ALIGNMENT_LAWFUL_BITS = [
  ENTITY_ALIGNMENT_LAWFUL_1, ENTITY_ALIGNMENT_LAWFUL_2,
  ENTITY_ALIGNMENT_LAWFUL_3, ENTITY_ALIGNMENT_LAWFUL_4,
] as const;
const ALIGNMENT_CHAOTIC_BITS = [
  ENTITY_ALIGNMENT_CHAOTIC_1, ENTITY_ALIGNMENT_CHAOTIC_2,
  ENTITY_ALIGNMENT_CHAOTIC_3, ENTITY_ALIGNMENT_CHAOTIC_4,
] as const;

export function alignmentGrade(entity: { readonly flags: number }): number {
  const lawful = ALIGNMENT_LAWFUL_BITS.filter((bit) => (entity.flags & bit) !== 0).length;
  const chaotic = ALIGNMENT_CHAOTIC_BITS.filter((bit) => (entity.flags & bit) !== 0).length;
  return 4 - lawful + chaotic;
}

/** The encoder side of `alignmentGrade`, mirroring `server/snapshot.py`'s
 *  `_alignment_grade_flags` -- exported so a fixture can build a realistic
 *  `flags` value for a given grade instead of hand-assembling bits. */
export function alignmentGradeFlags(grade: number): number {
  if (grade === 4) return 0;
  const steps = Math.abs(grade - 4);
  const bits = grade < 4 ? ALIGNMENT_LAWFUL_BITS : ALIGNMENT_CHAOTIC_BITS;
  return bits.slice(0, steps).reduce((flags, bit) => flags | bit, 0);
}

export const ENTITY_PET = 1 << 3;

/** Whether this entity is somebody's pet rather than a townsperson. */
export function isPet(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_PET) !== 0;
}

/** `EntityDelta.flags`, bit 13. Mirrors the branch-owned server allocation
 * `ENTITY_HORSE`: a standalone summoned horse NPC, distinct from a mounted
 * player and from a summoned pet. Its body is selected by `horseVnum`. */
export const ENTITY_HORSE = 1 << 13;

export function isHorse(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_HORSE) !== 0;
}

export const ENTITY_STEALTHED = 1 << 14;

export function isStealthed(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_STEALTHED) !== 0;
}

export const ENTITY_STUNNED = 1 << 25;

export function isStunned(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_STUNNED) !== 0;
}

/** `EntityDelta.flags`, bit 26. Mirrors `ENTITY_SPAWNING`: this stone arrived
 *  in the snapshot playing its SPAWN motion (`ADD_CHARACTER_STATE_SPAWN`,
 *  `InstanceBase.cpp:791-803`). */
export const ENTITY_SPAWNING = 1 << 26;

/** `CInstanceBase::CanPickInstance` (`InstanceBase.cpp:2273-2278`).
 *
 *  A stealthed PC is unpickable because `__MainCanSeeHiddenThing` is hardcoded
 *  false (`InstanceBase.cpp:686-691`). Other kinds are unchanged. */
export function canPickInstance(entity: {
  readonly kind: number;
  readonly flags: number;
}): boolean {
  return !(entity.kind === EntityKind.PLAYER && isStealthed(entity));
}

/** `PetCommand.command`. Mirrors `metin3/net/schema.py`'s `PET_DISMISS`, and it
 *  is the only command there is -- summoning is `useItem` on the summoning
 *  item, where the original puts it. */
export const PET_DISMISS = 0;

export const ENTITY_ARENA_MANAGER = 1 << 4;

/** Whether this entity will put a duel to another player. */
export function isArenaManager(entity: { readonly flags: number }): boolean {
  return (entity.flags & ENTITY_ARENA_MANAGER) !== 0;
}

export const ChatChannel = {
  SAY: 0,
  INFO: 1,
  NOTICE: 2,
  PARTY: 3,
  GUILD: 4,
  COMMAND: 5,
  SHOUT: 6,
  WHISPER: 7,
} as const;

export type ChatChannel = (typeof ChatChannel)[keyof typeof ChatChannel];

/** Health and level as the client tracks them. Zero `maxHp` means "not told". */
export interface Vitals {
  readonly hp: number;
  readonly maxHp: number;
  readonly level: number;
}

export const NO_VITALS: Vitals = { hp: 0, maxHp: 0, level: 0 };

/** The recipient's own progress. Named with a trailing underscore only to avoid
 *  colliding with the generated `Standing` message class. */
export interface Standing_ {
  readonly level: number;
  /** Toward the next level, not the lifetime total. */
  readonly exp: number;
  /** Zero at the level cap, which is how a UI knows to stop drawing a bar. */
  readonly expToNext: number;
  readonly maxHp: number;
  /** What this character hits and absorbs for, with whatever is worn. */
  readonly attackMin: number;
  readonly attackMax: number;
  readonly defence: number;
  /** The four primary statistics, as the server holds them. */
  readonly str: number;
  readonly dex: number;
  readonly int: number;
  readonly con: number;
  /** Status points left to spend. Derived server-side from the level and the
   *  four above, so it cannot disagree with them -- and never adjusted here: a
   *  panel that decremented this on click would show a point spent that the
   *  server refused. */
  readonly points: number;
  /** Percentages, where 100 is the baseline. Both are a flat hundred in the
   *  original too (`char.cpp:2190-2191`); shown because a character sheet that
   *  omitted speed would read as incomplete rather than as honest. */
  readonly attackSpeed: number;
  readonly moveSpeed: number;
  /** POINT_ENERGY. Zero when no energy affect is live. */
  readonly energy: number;
  /** Remaining ENERGY_END_TIME seconds. Zero when none. */
  readonly maxEnergy: number;
  /** POINT_STAT_RESET_COUNT (`uicharacter.py:730-731`). */
  readonly statResetCount: number;
  /** POINT_CASTING_SPEED, percent (`uicharacter.py:518`). */
  readonly castingSpeed: number;
  /** Magic Damage, both ends (`uicharacter.py:479-480`). */
  readonly magicAttackMin: number;
  readonly magicAttackMax: number;
  /** POINT_MAGIC_DEF_GRADE (`uicharacter.py:515`). */
  readonly magicDefence: number;
  /** Evading (`uicharacter.py:519`). */
  readonly evadeRate: number;
  /** The worn weapon's `POINT_MIN_WEP`..`POINT_MAX_MAGIC_WEP` (`PythonPlayer.cpp:418-421`). */
  readonly weaponMin: number;
  readonly weaponMax: number;
  readonly weaponMagicMin: number;
  readonly weaponMagicMax: number;
}

export const NO_STANDING: Standing_ = {
  level: 1,
  exp: 0,
  expToNext: 0,
  maxHp: 0,
  attackMin: 0,
  attackMax: 0,
  defence: 0,
  str: 0,
  dex: 0,
  int: 0,
  con: 0,
  points: 0,
  attackSpeed: 0,
  moveSpeed: 0,
  energy: 0,
  maxEnergy: 0,
  statResetCount: 0,
  castingSpeed: 0,
  magicAttackMin: 0,
  magicAttackMax: 0,
  magicDefence: 0,
  evadeRate: 0,
  weaponMin: 0,
  weaponMax: 0,
  weaponMagicMin: 0,
  weaponMagicMax: 0,
};

export const STAT_STR = 0;
export const STAT_DEX = 1;
export const STAT_INT = 2;
export const STAT_CON = 3;

export interface Spirit_ {
  readonly sp: number;
  readonly maxSp: number;
  /** Health a drunk potion still owes (`POINT_HP_RECOVERY`, `char.h:137`).
   *  Zero hides the overlay bar (`uitaskbar.py:681-682`). */
  readonly hpRecovery: number;
  /** Spirit still owed (`POINT_SP_RECOVERY`, `char.h:138`). */
  readonly spRecovery: number;
}

export const NO_SPIRIT: Spirit_ = { sp: 0, maxSp: 0, hpRecovery: 0, spRecovery: 0 };

/** One entry on the skill bar, as `SkillSlot` delivered it. Shaped for
 *  `ui/skillbar.ts` and mirrored nowhere else -- the panel is the only reader. */
export interface SkillSlot_ {
  readonly slot: number;
  readonly vnum: number;
  readonly level: number;
  readonly maxLevel: number;
  readonly spCost: number;
  readonly cooldownTicks: number;
  readonly cooldownLeft: number;
  readonly name: string;
  readonly icon: string;
  readonly flags: number;
}

/** The whole skill bar, from `Skills`. Never merged and never predicted: the
 *  server resends all of it on every change, exactly as it does for the bag. */
export interface Skills_ {
  readonly points: number;
  readonly horsePoints?: number;
  readonly slots: readonly SkillSlot_[];
}

export const NO_SKILLS: Skills_ = { points: 0, slots: [] };

export interface QuickSlot_ {
  readonly pos: number;
  readonly type: number;
  readonly target: number;
}

/** No quick slot set: what a fresh character has, and what a `QuickSlots`
 *  frame that emptied the last one says. */
export const NO_QUICK_SLOTS: readonly QuickSlot_[] = [];

/** One live timed modifier, from `AffectSlot`. `remaining` is a duration in
 *  ticks at the moment the frame was sent, never a deadline. */
export interface Affect_ {
  readonly source: number;
  readonly apply: number;
  readonly value: number;
  readonly remaining: number;
}

export const PVP_PEACE = 0;
export const PVP_REVENGE = 1;
export const PVP_FREE = 2;
export const PVP_PROTECT = 3;
export const PVP_GUILD = 4;

export const NO_PVP_MODE = PVP_PEACE;

/** `EPVPModes` (`packet.h:1355-1361`), with the original's numbering. */
export const DUEL_MODE_NONE = 0;
export const DUEL_MODE_AGREE = 1;
export const DUEL_MODE_FIGHT = 2;
export const DUEL_MODE_REVENGE = 3;

export interface DuelRecord {
  readonly srcId: number;
  readonly dstId: number;
  readonly mode: number;
}

export type DuelState =
  | "none"
  | "challenged"
  | "revenge"
  | "waiting"
  | "fighting";

/** The pair's key, order-independent -- the original's CRC over the sorted
 *  pair (`pvp.cpp:32-35`) without the collision. */
export function duelKey(one: number, two: number): string {
  return one > two ? `${one}:${two}` : `${two}:${one}`;
}

export function applyDuelFrame(
  duels: Map<string, DuelRecord>,
  srcId: number,
  dstId: number,
  mode: number,
): void {
  const key = duelKey(srcId, dstId);
  if (mode === DUEL_MODE_NONE) {
    duels.delete(key);
    return;
  }
  duels.set(key, { srcId, dstId, mode });
}

/** What this client may do about `otherId`, given the whole duel table and
 *  this client's own vid. See `DuelState`. */
export function duelStateWith(
  duels: ReadonlyMap<string, DuelRecord>,
  selfId: number,
  otherId: number,
): DuelState {
  const record = duels.get(duelKey(selfId, otherId));
  if (record === undefined) return "none";
  if (record.mode === DUEL_MODE_FIGHT) return "fighting";
  // For AGREE and REVENGE the asked side is `dst` and the asking side is
  // `src`. `uitarget.py:423-429` prefers the revenge label over the accept
  // label when both could apply, and hides the plain Duel button in either.
  if (record.dstId !== selfId) return "waiting";
  return record.mode === DUEL_MODE_REVENGE ? "revenge" : "challenged";
}

/** One occupied inventory slot, as the server sent it. */
/** One line of an NPC's speech. `kind` picks the ink, not the meaning. */
export interface QuestLine_ {
  /** 0 speech, 1 reward, 2 an item being shown. */
  readonly kind: number;
  /** Kind 2 only: which item's icon to draw. */
  readonly itemVnum: number;
  readonly text: string;
}

/** One page of a conversation, exactly as the server composed it. */
export interface QuestDialog_ {
  /** Echoed back with the answer. Zero means "close whatever is open". */
  readonly token: number;
  readonly title: string;
  /** True when dismissing this ends the conversation. */
  readonly final: boolean;
  readonly lines: readonly QuestLine_[];
  /** Empty for a page that only continues. */
  readonly choices: readonly string[];
  /** The window it opens in (`questmanager.cpp:947`); absent is NORMAL (`questmanager.cpp:86`). */
  readonly skin?: number;
}

/** The script page which ends in `input()`. */
export interface QuestInputPrompt_ {
  readonly token: number;
  readonly title: string;
  readonly maxBytes: number;
  readonly lines: readonly QuestLine_[];
}

/** A quest's cross-player yes/no question, or the requester's waiting face. */
export interface QuestConfirmPrompt_ {
  readonly token: number;
  readonly text: string;
  readonly remainingMs: number;
  readonly waiting: boolean;
}

/** The cells the server says are worth drawing in the quest item chooser. */
export interface QuestItemPrompt_ {
  readonly token: number;
  readonly cells: readonly number[];
}

/** One row of the quest log. */
export interface QuestEntry_ {
  /** The server's own index. What `questInfo` names -- never a quest name. */
  readonly index: number;
  readonly title: string;
  readonly counterName: string;
  readonly counter: number;
  /** The server's label for a timed objective, if this row has one. */
  readonly clockName: string;
  /** Server-derived seconds remaining when this log snapshot was sent. */
  readonly clockSeconds: number;
  /** The entity this quest is pointing at, or 0. */
  readonly targetId: number;
  readonly targetDesc: string;
}

/** One row of the map screen's NPC roster (`packet.h:1917-1923`). The point is
 *  where the map file placed the row, not where the entity now stands. */
export interface NpcMarker_ {
  /** `CHAR_TYPE_NPC` (1), `CHAR_TYPE_WARP` (3) or `CHAR_TYPE_GOTO` (9). */
  readonly kind: number;
  readonly name: string;
  readonly xMm: number;
  readonly yMm: number;
}

/** A server-owned quest arrow change; no client message can create one. */
export type QuestTarget_ =
  | { readonly kind: "create"; readonly id: number; readonly xMm: number; readonly yMm: number; readonly description: string }
  | { readonly kind: "update"; readonly id: number; readonly xMm: number; readonly yMm: number }
  | { readonly kind: "delete"; readonly id: number };

export const QUEST_LINE_SAY = 0;
export const QUEST_LINE_REWARD = 1;
export const QUEST_LINE_ITEM = 2;
/** `questlib.lua:42,45` and `questlua_global.cpp:94-112` BGIMAGE/IMAGE/
 *  LEFTIMAGE/TOPIMAGE, plus the TITLE_IMAGE, INSERT_IMAGE and WINDOW_SIZE tags
 *  the same stream carries. A dialogue-window instruction, not speech. */
export const QUEST_LINE_IMAGE = 3;
/** `questlib.lua:36,39,95-99` ADDMAPSIGNAL/CLEARMAPSIGNAL/SETCMAPPOS. A
 *  minimap instruction, not speech. */
export const QUEST_LINE_MAPSIGNAL = 4;
/** `questlib.lua:26-30` DELAY tag. Timing, not speech. */
export const QUEST_LINE_DELAY = 5;
/** `questlib.lua:27` COLOR tag. Subsequent letter ink, not speech. */
export const QUEST_LINE_COLOR = 6;
/** FADE_OUT/FADE_IN/WHITE_OUT/WHITE_IN, which `EventCurtain` runs over the
 *  whole screen (`uiquest.py:120-204`). A screen instruction, not speech. */
export const QUEST_LINE_CURTAIN = 7;

/** `QuestDialog.token` meaning "close whatever is open". */
export const QUEST_NO_DIALOG = 0;

/** One cell of the inventory: which cell, which item, and what that item is
 *  (`ItemFacts`, `client/src/itemfacts.ts`). */
export interface BagSlot_ extends ItemFacts {
  readonly slot: number;
  readonly vnum: number;
  readonly count: number;
  readonly name: string;
  /** The same icon byte as a ground item's `itemKind`. */
  readonly kind: number;
  /** What a dealer would pay for this whole slot, already rounded server-side.
   *  Zero means nobody will buy it. Never recompute this from `vnum` and
   *  `count` -- the rounding is on the total, and a client's guess is a quote
   *  the server will refuse. */
  readonly sellValue: number;
}

/** The whole inventory. A `Bag` frame replaces it; an `ItemSet`, `ItemDel` or
 *  `Gold` frame amends the one thing it names, which is how the server answers
 *  every change after the first (`char_item.cpp:386-412`). */
export interface Inventory {
  readonly gold: number;
  readonly capacity: number;
  readonly slots: readonly BagSlot_[];
  /** What the character is wearing. `slot` here is a wear slot, not a bag index. */
  readonly worn: readonly BagSlot_[];
}

/** The server's answer to a fieldless request to inspect the currently selected
 * player.  It is deliberately separate from `Inventory`: this is another
 * player's read-only body, never a container the browser can move from. */
export interface EquipmentView_ {
  readonly entityId: number;
  readonly name: string;
  readonly worn: readonly BagSlot_[];
}

export const NO_BAG: Inventory = { gold: 0, capacity: 0, slots: [], worn: [] };

/** `EWindows.EQUIPMENT` (`length.h:450-463`). `ItemSet.window` is the original's
 *  `TItemPos::window_type`: anything else names a bag cell, belt cells included
 *  -- this build numbers those inside the bag, as `char_item.cpp:519` does. */
const EQUIPMENT_WINDOW = 2;

/** A cell as either frame carries it: `Bag`'s own struct, or the flat `ItemSet`
 *  that repeats its fields behind a window byte. */
type ItemCell = Omit<BagSlot, "encode">;

/** The bag with one cell replaced, kept in the ascending order a whole `Bag`
 *  arrives in so that nothing downstream can tell the two apart. */
function withCell(bag: Inventory, window: number, cell: BagSlot_): Inventory {
  const worn = window === EQUIPMENT_WINDOW;
  const strip = [...(worn ? bag.worn : bag.slots).filter((row) => row.slot !== cell.slot), cell];
  strip.sort((a, b) => a.slot - b.slot);
  return worn ? { ...bag, worn: strip } : { ...bag, slots: strip };
}

/** The bag with one cell emptied. */
function withoutCell(bag: Inventory, window: number, slot: number): Inventory {
  const worn = window === EQUIPMENT_WINDOW;
  const strip = (worn ? bag.worn : bag.slots).filter((row) => row.slot !== slot);
  return worn ? { ...bag, worn: strip } : { ...bag, slots: strip };
}

function bagSlotOf(s: ItemCell): BagSlot_ {
  return {
    slot: s.slot,
    vnum: s.vnum,
    count: s.count,
    name: s.name,
    kind: s.kind,
    sellValue: s.sell_value,
    ...itemFactsOf(s),
  };
}


/** One shelf on a dealer's counter. `ItemStats` and not `ItemFacts`: a shelf
 *  is a row of the proto table, so it has no rolled bonuses and no sockets --
 *  those belong to a particular copy of the item, and the shelf has none yet. */
export interface ShopSlot_ extends ItemStats {
  /** The cell in the server's own shop grid. What `buyItem` names -- never this
   *  row's position in the array, which is a different number as soon as two
   *  items have different heights. */
  readonly slot: number;
  readonly vnum: number;
  /** How many units one purchase hands over. A shelf is a bundle. */
  readonly count: number;
  readonly name: string;
  /** The same icon byte as a bag slot's `kind`. */
  readonly kind: number;
  /** Gold for the whole shelf, already multiplied server-side. Never recompute
   *  it from `count`: the `COUNT_PER_1GOLD` items divide where this multiplies,
   *  and a client's guess is a price the server would refuse. */
  readonly price: number;
}

/** What a dealer is selling, exactly as last sent. `npcId` of 0 means no
 *  counter is open -- the server's own way of saying "closed". */
export interface ShopStock {
  readonly npcId: number;
  readonly name: string;
  readonly items: readonly ShopSlot_[];
}

export const NO_SHOP: ShopStock = { npcId: 0, name: "", items: [] };

export type {
  RefineOffer,
  RefineResult,
  ExchangeState,
  Safebox,
  GuildFrame,
  GuildLandStateFrame,
  LandListFrame,
  GuildInvitationFrame,
  GuildWarMenuFrame,
  GuildWarArenaFrame,
  GuildCommentsFrame,
  PartyFrame,
  PartyInvitationFrame,
  CharactersFrame,
  EmpireSelectPrompt,
  EmotePlayedFrame,
  EmoteRequestFrame,
  MountFrame,
  FishingFrame,
  FishCaughtFrame,
  FishingActFrame,
  MiningFrame,
  MineResultFrame,
  OXQuestionFrame,
  OXResultFrame,
  AutoBanQuizFrame,
  DungeonStateFrame,
  WarpFrame,
  MapChangeFrame,
  PetSummonedFrame,
  PolymorphedFrame,
  // ---- arena
  ArenaDuelFrame,
  ArenaInvitationFrame,
  ArenaResultFrame,
  // ---- a player's own stall
  StallSignFrame,
  StallSoldFrame,
  StallWindowFrame,
  // ---- the cube
  CubeRecipes,
  CubeResult,
  CubeClosed,
  // ---- dragon souls
  DragonSouls,
  DragonSoulRefined,
  // ---- target
  TargetedFrame,
  AimedFrame,
  // ---- marriage and the friend list
  MarriageStatusFrame,
  FriendListFrame,
  MonarchInfoFrame,
  // ---- the auction house and the item mall
  AuctionBoardFrame,
  AuctionNewsFrame,
  MallWindowFrame,
};

/** An empty dragon soul window: no souls, no Alchemy, no deck. Not the same as
 *  "not told yet", which is why the server sends this frame on login even when
 *  it is empty -- see `DragonSouls`. `active_deck` is 255 rather than 0,
 *  because 0 is deck one. */
export const NO_SOULS: DragonSouls = Object.assign(new DragonSouls(), {
  qualified: false,
  active_deck: 255,
  cells: [],
});

/** Nobody is pointing at anything. A `target` of zero is the server's own way
 *  of saying so -- `CHARACTER::ClearTarget` sends `dwVID = 0`
 *  (`char.cpp:4875-4895`) -- so this is the frame's own resting state rather
 *  than a sentinel invented here. */
export const NO_TARGETED: TargetedFrame = new TargetedFrame();

/** Nobody is married and nobody is asking. A `state` of zero is the server's
 *  own way of saying so, and it is what a divorce, a declined proposal and a
 *  fresh login all arrive as -- there is no separate "it ended" frame, which is
 *  what `lover_divorce` is in the original (`marriage.cpp:53-63`). */
export const NO_MARRIAGE: MarriageStatusFrame = new MarriageStatusFrame();

/** An empty friend list with both switches off. What a client holds before its
 *  first `FriendList` and what a guest is sent -- see `social.py`. */
export const NO_FRIENDS: FriendListFrame = new FriendListFrame();

/** Nobody has challenged this player. A `token` of zero is the server saying
 *  the challenge that was on screen is gone -- answered, expired, or its
 *  challenger logged out -- and a client must never send a zero token back,
 *  because the server never mints one. */
export const NO_ARENA_INVITATION: ArenaInvitationFrame = new ArenaInvitationFrame();

/** In no duel, and watching none. A `duel_id` of zero is the server's own way
 *  of saying so, and it is the original's too: `HEADER_GC_DUEL_START` with an
 *  empty opponent list is how duel mode is switched off
 *  (`PythonNetworkStreamPhaseGame.cpp:929-933`, `input_login.cpp:674-678`). */
export const NO_ARENA_DUEL: ArenaDuelFrame = new ArenaDuelFrame();

export const NO_STALL: StallWindowFrame = new StallWindowFrame();

/** Nothing on the auction board. The starting value and what a reconnect goes
 *  back to -- never a claim that the board is empty, only that this client has
 *  not asked. `openAuction` is the only thing that fills it. */
export const NO_AUCTION: AuctionBoardFrame = new AuctionBoardFrame();

/** Nothing in this account's mall, which is the normal case: this build has no
 *  payment system and nothing a player can do puts an item there. See
 *  `metin3/server/mall.py`. */
export const NO_MALL: MallWindowFrame = new MallWindowFrame();

/** `AuctionLot.claim`: what `claimLot` would do for this viewer. Sent by the
 *  server, never decided here -- a client that worked this out for itself
 *  would be a client asserting a role, which is the whole thing `ClaimLot`
 *  does not carry. */
export const AuctionClaim = {
  NOTHING: 0,
  ITEM: 1,
  PROCEEDS: 2,
  WITHDRAW: 3,
  REFUND: 4,
} as const;

/** `AuctionNews.event`. Two things can happen to a lot you have a stake in
 *  while you are looking somewhere else. */
export const AuctionEvent = {
  OUTBID: 1,
  SOLD: 2,
} as const;

/** No line in the water. A `state` of zero is the server's own way of saying
 *  so, and the rod fields are then whatever it last found in the bag. */
export const NO_FISHING: FishingFrame = new FishingFrame();

/** No horse. A `level` of zero is the server's own way of saying so, and the
 *  speeds on it are then the character's own -- see `Mount`. */
export const NO_MOUNT: MountFrame = new MountFrame();

/** No pick in the air. A `state` of zero is the server's own way of saying so,
 *  and every other field on the frame is then zero as well. */
export const NO_MINING: MiningFrame = new MiningFrame();

/** Not in a dungeon and not standing at a door. `inside` false with a `door` of
 *  zero is the server's own way of saying both, and a default-constructed frame
 *  is exactly that -- which is why the panel can be built before a socket is
 *  open and draw nothing. */
export const NO_DUNGEON: DungeonStateFrame = new DungeonStateFrame();

/** Not transformed. An empty `model` is the server's own way of saying so, on
 *  this frame and on `EntityDelta.polymorph` alike -- and it is a *value* in
 *  both places rather than an absence, because a delta struct's absent field
 *  means unchanged. See `Polymorphed`. */
export const NOT_POLYMORPHED: PolymorphedFrame = new PolymorphedFrame();

/** No pet out. An `entity_id` of zero, likewise. */
export const NO_PET: PetSummonedFrame = new PetSummonedFrame();

/** Nobody is waiting on an answer. A `token` of zero is the server saying the
 *  request that was on screen is gone -- allowed, refused, or its asker logged
 *  out -- and a client must never send a zero token back, because the server
 *  never mints one. */
export const NO_EMOTE_REQUEST: EmoteRequestFrame = new EmoteRequestFrame();

/** No trade is open. `partner_id` of zero is the server's own way of saying so. */
export const NO_EXCHANGE: ExchangeState = new ExchangeState();

/** No storeroom is open. `npc_id` of zero, likewise. */
export const NO_SAFEBOX: Safebox = new Safebox();

/** Not in a party. `leader_id` of zero is the server's own way of saying so. */
export const NO_PARTY: PartyFrame = new PartyFrame();

/** Not in a guild. `guild_id` of zero is the server's own way of saying so. */
export const NO_GUILD: GuildFrame = new GuildFrame();

/** No guild invitation on screen. A `token` of zero is the server saying the
 *  one that was there is gone -- answered, declined, run out, or its inviter
 *  logged out. */
export const NO_GUILD_INVITATION: GuildInvitationFrame = new GuildInvitationFrame();

/** No invitation is standing. `token` of zero, likewise -- and a client must
 *  never send a zero token back: the server never mints one. */
export const NO_INVITATION: PartyInvitationFrame = new PartyInvitationFrame();

/** The health fields of an `EntityDelta`, structurally. */
export interface VitalsDelta {
  readonly hp: number | null;
  readonly max_hp: number | null;
  readonly level: number | null;
}

export function mergeVitals(previous: Vitals | undefined, delta: VitalsDelta): Vitals {
  const base = previous ?? NO_VITALS;
  return {
    hp: delta.hp ?? base.hp,
    maxHp: delta.max_hp ?? base.maxHp,
    level: delta.level ?? base.level,
  };
}

/** One remote entity as the renderer sees it. */
export interface RemoteEntity {
  id: number;
  name: string;
  kind: number;
  xMm: number;
  yMm: number;
  heading: number;
  gait: Gait;
  hp: number;
  maxHp: number;
  level: number;
  /** How to draw a thing on the ground: 1 weapon, 2 armour, 3 potion, 4 other.
   *  Zero on anything that is not loot. */
  itemKind: number;
  /** Server-owned name and deadline for a ground-item claim. Empty/zero on
   *  everything else. They are display data only; pickup authority never moved
   *  out of `GroundItems.take`. */
  itemOwner: string;
  itemPublicAtTick: number;
  /** Sockets 0, 1 and 2 on the server-owned ground item. They are visual state
   * only: a pickup request names the entity, and the server reads the item. */
  itemSocket: number;
  itemSocket1: number;
  itemSocket2: number;
  /** `EntityDelta.flags`. Only bit 0 is defined so far -- see
   *  `ENTITY_SHOPKEEPER` -- and it is the only way to tell a dealer from a
   *  guard, both of which are otherwise identical NPCs. */
  flags: number;
  /** The item vnum in this character's right hand, or 0 for empty-handed.
   *  `render/creaturemesh.ts`'s `itemModelKey` turns it into a mesh. */
  weapon: number;
  /** The item vnum of this character's body armour, or 0 for the novice body.
   *  `bodyModelKey` turns it into the mesh that *replaces* his body. */
  armour: number;
  race: number;
  /** `PART_HAIR`. Zero is novice/bleached. Dye writes 1-5; costume styles
   *  are >= 1001. Zero is a real value (`??`, not `||`). */
  hair: number;
  polymorph: string;
  mount: number;

  gmLevel: number;

  guildId: number;
  /** Server-resolved `object_proto` vnum for a durable guild building, or 0. */
  buildingVnum: number;
  /** Body vnum for a standalone summoned horse, or 0 on every other entity. */
  horseVnum: number;
  /** `EntityDelta.mob_vnum`: the monster prototype `GetMonsterColor` keys a
   *  body's tint by, or 0 on a player. */
  mobVnum: number;
  /** `EntityDelta.affect_flags`: bit `i` is `CInstanceBase` affect index `i`. */
  affectFlags: number;
}

/** One server sample of one remote body. `render/remotemotion.ts` measures the
 *  body's own speed off a run of these; nothing slides between two of them any
 *  more. */
type Sample = MotionSample;

export class EntityInterpolator {
  private readonly trails = new Map<number, Sample[]>();
  private readonly latest = new Map<number, RemoteEntity>();
  /** One drawn body per entity, and one object to report it in. Both are
   *  long-lived on purpose: `render` runs once a frame per entity and used to
   *  spread a twenty-five field object per entity per call. */
  private readonly bodies = new Map<number, RemoteMotion>();
  private readonly drawn = new Map<number, RemoteEntity>();
  private readonly out: RemoteEntity[] = [];
  private readonly scratch: DrawnMotion = { xMm: 0, yMm: 0, heading: 0, gait: Gait.IDLE };
  private lastRenderMs: number | null = null;

  /** Apply a delta on top of whatever this entity was, and stamp a sample. */
  observe(delta: EntityDelta, timeMs: number): void {
    const previous = this.latest.get(delta.id);
    // Health is merged, never interpolated: hit points are a step function and
    // sliding one between two snapshots would draw a bar that is briefly a value
    // the server never held.
    const vitals = mergeVitals(previous, delta);
    const entity: RemoteEntity = {
      id: delta.id,
      name: delta.name ?? previous?.name ?? "",
      kind: delta.kind ?? previous?.kind ?? 0,
      xMm: delta.x_mm ?? previous?.xMm ?? 0,
      yMm: delta.y_mm ?? previous?.yMm ?? 0,
      heading: delta.heading ?? previous?.heading ?? 0,
      gait: toGait(delta.gait ?? previous?.gait ?? Gait.IDLE),
      hp: vitals.hp,
      maxHp: vitals.maxHp,
      level: vitals.level,
      itemKind: delta.item_kind ?? previous?.itemKind ?? 0,
      itemOwner: delta.item_owner ?? previous?.itemOwner ?? "",
      itemPublicAtTick:
        delta.item_public_at_tick ?? previous?.itemPublicAtTick ?? 0,
      itemSocket: delta.item_socket ?? previous?.itemSocket ?? 0,
      itemSocket1: delta.item_socket1 ?? previous?.itemSocket1 ?? 0,
      itemSocket2: delta.item_socket2 ?? previous?.itemSocket2 ?? 0,
      flags: delta.flags ?? previous?.flags ?? 0,
      weapon: delta.weapon ?? previous?.weapon ?? 0,
      armour: delta.armour ?? previous?.armour ?? 0,
      race: delta.race ?? previous?.race ?? 0,
      hair: delta.hair ?? previous?.hair ?? 0,
      // `??` again, and the same trap: an empty string here is a
      // transformation that ended and has to overwrite the model that was
      // there. `||` would read it as falsy and keep the monster.
      polymorph: delta.polymorph ?? previous?.polymorph ?? "",
      mount: delta.mount ?? previous?.mount ?? 0,
      // `??`, not `||`: zero is a mark taken away, and the encoder resends it
      // explicitly for that reason (`snapshot.py`'s `_changed`).
      gmLevel: delta.gm_level ?? previous?.gmLevel ?? 0,
      // `??`, not `||`: zero is a guild left, and the encoder resends it
      // explicitly for that reason (`snapshot.py`'s `_changed`).
      guildId: delta.guild_id ?? previous?.guildId ?? 0,
      buildingVnum: delta.building_vnum ?? previous?.buildingVnum ?? 0,
      horseVnum: delta.horse_vnum ?? previous?.horseVnum ?? 0,
      mobVnum: delta.mob_vnum ?? previous?.mobVnum ?? 0,
      // `??`, not `||`: zero is the last buff lapsing.
      affectFlags: delta.affect_flags ?? previous?.affectFlags ?? 0,
    };
    this.latest.set(delta.id, entity);

    const trail = this.trails.get(delta.id) ?? [];
    // Out-of-order arrival is possible on an unreliable transport; a sample older
    // than one already held would make the entity walk backwards, so drop it.
    if (trail.length > 0 && timeMs <= trail[trail.length - 1].timeMs) return;
    trail.push({
      timeMs,
      xMm: entity.xMm,
      yMm: entity.yMm,
      heading: entity.heading,
      gait: entity.gait,
    });
    while (trail.length > 8) trail.shift();
    this.trails.set(delta.id, trail);

    const sample = trail[trail.length - 1];
    let body = this.bodies.get(delta.id);
    if (body === undefined) {
      body = new RemoteMotion();
      this.bodies.set(delta.id, body);
    }
    body.observe(sample, trail);
  }

  /** `CNetworkActorManager::SyncActor` (`NetworkActorManager.cpp:539-557`): the
   *  server moved `id` by force. The body's newest known position becomes the
   *  new one, and its drawn body is `__Push`ed there (`ActorInstanceSync.cpp:7-53`).
   *  `AFFECT_CHEONGEUN`, which `IsResistFallen` reads (`InstanceBaseEffect.cpp:892-893`),
   *  is on no frame about another body, so it reads false; `usingSkill` is
   *  whether the body plays a skill motion (`IsUsingSkill`, `ActorInstanceMotion.cpp:433-451`).
   *  Null for a body this client has not seen. */
  push(
    id: number, xMm: number, yMm: number, blocked: (xMm: number, yMm: number) => boolean, usingSkill = false,
  ): PushOutcome | null {
    const body = this.bodies.get(id);
    const known = this.latest.get(id);
    if (body === undefined || known === undefined) return null;
    this.latest.set(id, { ...known, xMm, yMm }); // rkNetActorData.SetPosition, NetworkActorManager.cpp:556
    const outcome = pushOutcome({
      fromXMm: body.xMm, fromYMm: body.yMm, toXMm: xMm, toYMm: yMm,
      resistsFall: false, usingSkill, blocked,
    });
    if (outcome.blend) body.push(xMm, yMm);
    else body.resetPush();
    return outcome;
  }

  remove(id: number): void {
    this.trails.delete(id);
    this.latest.delete(id);
    this.bodies.delete(id);
    this.drawn.delete(id);
  }

  forget(): void {
    this.trails.clear();
    this.latest.clear();
    this.bodies.clear();
    this.drawn.clear();
    this.lastRenderMs = null;
  }

  get size(): number {
    return this.latest.size;
  }

  /** The newest known state of one entity, as the server last described it.
   *  Used by targeting, by the HUD and by the death effect, none of which want
   *  the drawn position -- they want the last thing the server actually
   *  said. */
  get(id: number): RemoteEntity | null {
    return this.latest.get(id) ?? null;
  }

  /** Every id currently in the world, for pruning a stale target. */
  ids(): ReadonlySet<number> {
    return new Set(this.latest.keys());
  }

  render(nowMs: number, excludeId: number | null = null): RemoteEntity[] {
    const dtMs = this.lastRenderMs === null ? 0 : Math.max(0, nowMs - this.lastRenderMs);
    this.lastRenderMs = nowMs;
    this.out.length = 0;

    for (const [id, entity] of this.latest) {
      if (id === excludeId) continue;
      const body = this.bodies.get(id);
      if (body === undefined) continue;
      const motion = body.advance(dtMs, this.scratch);
      let seen = this.drawn.get(id);
      if (seen === undefined) {
        seen = { ...entity };
        this.drawn.set(id, seen);
      } else {
        Object.assign(seen, entity);
      }
      seen.xMm = motion.xMm;
      seen.yMm = motion.yMm;
      seen.heading = motion.heading;
      seen.gait = motion.gait;
      this.out.push(seen);
    }
    return this.out;
  }
}

function toGait(value: number): Gait {
  if (value === Gait.RUN) return Gait.RUN;
  if (value === Gait.WALK) return Gait.WALK;
  return Gait.IDLE;
}

export interface ConnectionOptions {
  url: string;
  playerName: string;
  credentials?: { username: string; password: string } | null;
  crossing?: string | null;
  registration?: { username: string; password: string } | null;
  /** The collision world. `ObjectWorld`, not `World`: this connection is
   *  the only writer of the runtime ATTR_OBJECT overlay, restamping it from
   *  the guild-building footprints each snapshot carries. */
  world: ObjectWorld;
  /** Every guild building's `.mdatr` records, which a standing building's
   *  footprint places into `world` beside its ATTR_OBJECT cells; null or absent
   *  places none. A getter, since the table loads after the connection exists. */
  guildAttributes?: () => GuildAttributeTable | null;
  /** Whether the body `entityId` plays a skill motion (`IsUsingSkill`,
   *  `ActorInstanceMotion.cpp:433-451`), which a shove blends without knocking
   *  down (`ActorInstanceSync.cpp:43`). Absent, none plays one. */
  usingSkill?: (entityId: number) => boolean;
  input: InputSource;
  stats?: MoveStats;
  build?: string;
  /** Injected so tests can drive the clock; defaults to the wall clock. */
  now?: () => number;
  onInputTick?: () => void;
  onStatus?: (status: ConnectionStatus, detail: string) => void;
  /** The server-owned outdoor light mode. There is no sender for it. */
  onEnvironment?: (night: boolean) => void;
  /** The deployment-owned weather pass. There is no client sender for it. */
  onWeather?: (kind: number) => void;
  /** Closed track index and factor selected by the shard. No sender exists. */
  onFieldMusic?: (track: number, volumePercent: number) => void;
  /** One line of chat, with the `ChatChannel` that decided who heard it. The
   *  channel arrives for colour: by the time a line lands, the reach that
   *  produced it is spent and there is nothing else in the frame to tell a
   *  shout from a party line. */
  onChat?: (from: string, text: string, channel: number) => void;
  /** One whisper. Its own callback because the frame carries no entity id and
   *  never will -- a whisper crosses the map to somebody this client has not
   *  been told about, and an id would be a position lookup. */
  onWhisper?: (fromName: string, text: string, refusal: boolean) => void;
  /** Fired only when a level was actually gained, never on a plain award. */
  onLevel?: (level: number, gained: number) => void;
  /** One resolved swing, as the server judged it. Delivered raw; deciding what it
   *  looks like is `client/combat.ts`, which knows nothing about the wire. */
  onCombat?: (event: CombatResolution) => void;
  /** One indexed mote in flight between two entities, by `FLY_*` type. */
  onCreateFly?: (fly: FlyLaunch) => void;
  /** A body the server moved by force, and where it now stands
   *  (`CHARACTER::SyncPacket`, `char.cpp:6249-6267`). */
  onSyncPosition?: (entityId: number, shove: PushOutcome) => void;
  /** A body in view, never this client's own, began a skill motion: a player's
   *  skill by vnum and grade, a monster's by slot (`Snapshot.skill_motions`,
   *  `RecvCharacterMovePacket`'s skill functions, `InstanceBase.cpp:1464-1492,1524-1554`). */
  onSkillMotion?: (entityId: number, motion: number, grade: number) => void;
  /** The whole inventory, every time it changes. */
  onBag?: (bag: Inventory) => void;
  /** The server found a live reset scroll and asks the first question. There is
   *  deliberately no item, cost or outcome here. */
  onStatusResetOffer?: () => void;
  /** The first reset question was accepted; this is the final question. */
  onStatusResetConfirm?: () => void;
  /** This character's player-versus-player mode, as the server holds it. Fired
   *  on join and after every request, accepted or not -- so a control bound to
   *  it shows the server's answer rather than the click. */
  onPvpMode?: (mode: number) => void;
  /** The character sheet, every time any of it changes -- a level, a spent
   *  point, a piece of gear. Fired for refused spends too, carrying the
   *  unchanged sheet, which is what lets a panel repaint the truth. */
  onStanding?: (standing: Standing_) => void;
  /** The spirit pool. Fires on join and whenever it regenerates. */
  onSpirit?: (spirit: Spirit_) => void;
  /** The whole skill bar. Fires on join, on every cast -- accepted or refused --
   *  and on every skill point spent. A refused cast is why this fires at all:
   *  it is how the bar learns it was on cooldown. */
  onSkills?: (skills: Skills_) => void;
  /** The whole quick-slot table. Fires on join and after every change the
   *  server made -- including the ones it made on its own, when `SyncQuickslot`
   *  re-pointed a slot at the cell its item moved to
   *  (`char_quickslot.cpp:12-34`). */
  onQuickSlots?: (slots: readonly QuickSlot_[]) => void;
  /** Every timed modifier on this character. Fires when the set changes, not
   *  every tick; the countdown is the client's to run. */
  onAffects?: (affects: readonly Affect_[]) => void;
  onSpecialEffect?: (entityId: number, effect: number) => void;
  /** `RecvSpecificEffect` (`PythonNetworkStreamPhaseGameItem.cpp:894-909`): one effect file on one body. */
  onSpecificEffect?: (entityId: number, effectFile: string) => void;
  /** `RecvPointChange` for a body in view (`PythonNetworkStreamPhaseGame.cpp:1427-1497`). */
  onPointChange?: (entityId: number, point: number, value: number) => void;
  /** Why something the player asked for did not happen. */
  onNotice?: (code: number, text: string, big: boolean) => void;
  onCharacters?: (characters: CharactersFrame) => void;
  /** The authenticated account has no persisted empire; choose one before
   *  character select. The mask is server-provided presentation only. */
  onEmpireSelect?: (prompt: EmpireSelectPrompt) => void;
  /** The answer to a `register`. Fires once per attempt, either way. */
  onRegistered?: (ok: boolean, username: string, text: string) => void;
  /** One page of an NPC conversation, or a token of zero meaning "close".
   *  Fires only when the server has something to say: a click on an NPC with no
   *  quest for you produces no frame at all, which is how the client knows to
   *  fall back to whatever else that NPC is (a dealer, usually). */
  onQuestDialog?: (page: QuestDialog_) => void;
  onQuestInput?: (prompt: QuestInputPrompt_) => void;
  onQuestConfirm?: (prompt: QuestConfirmPrompt_) => void;
  onQuestItem?: (prompt: QuestItemPrompt_) => void;
  /** Every open quest, in full, whenever any of it changes. */
  onQuestLog?: (entries: readonly QuestEntry_[]) => void;
  onQuestTarget?: (event: QuestTarget_) => void;
  /** The map screen's NPC roster, sent once at join
   *  (`sectree_manager.cpp:1051-1090`, `input_login.cpp:548`). Never sent
   *  again, so a client that missed it keeps whatever it already had. */
  onNpcPositions?: (markers: readonly NpcMarker_[]) => void;
  /** What a dealer sells, in answer to `clickNpc`. An `npcId` of zero means the
   *  server has closed the counter -- because the player walked away, died, or
   *  asked it to. Never fired unprompted. */
  onShop?: (stock: ShopStock) => void;
  /** What a blacksmith would charge for one bag slot, in answer to
   *  `refineInfo`. Never fired unprompted, and never for a refusal: an item the
   *  smith will not touch produces a `Notice` and no offer, because a window
   *  whose only button is Cancel is a window that should not have opened. */
  onRefineOffer?: (offer: RefineOffer) => void;
  /** What the roll did, after `refine`. Arrives immediately before the `Bag`
   *  that is the actual truth -- this carries the two names, because "Sword+8
   *  was destroyed" cannot be reconstructed from a bag that no longer has one. */
  onRefineResult?: (result: RefineResult) => void;
  /** Everything one cube counter can build, answered to `cubeInfo`. Never
   *  unprompted, and never for a refusal: a counter that builds nothing sends
   *  a `Notice` instead, because a window whose only button is Cancel is a
   *  window nobody wanted opened. */
  onCubeRecipes?: (recipes: CubeRecipes) => void;
  /** What one build did, after `cubeMake`. Arrives immediately before the
   *  `Bag` that is the truth about it. */
  onCubeResult?: (result: CubeResult) => void;
  /** The server closed the cube window, for example when this player died. */
  onCubeClosed?: (closed: CubeClosed) => void;
  /** The whole dragon soul window, sparse, whenever any of it changes. Sent on
   *  login too, so "no souls" and "not told yet" are never the same reading. */
  onDragonSouls?: (souls: DragonSouls) => void;
  /** What one refinement did, after `dragonSoulRefine`. Arrives immediately
   *  before the `DragonSouls` that is the truth about it. */
  onDragonSoulRefined?: (result: DragonSoulRefined) => void;
  /** The whole trade window, both sides, whenever any of it changes. A
   *  `partner_id` of zero means no trade is open -- and `settled` on that frame
   *  is the one thing distinguishing a trade that went through from one the
   *  other player walked out of. */
  onExchange?: (state: ExchangeState) => void;
  /** The whole storeroom, whenever any of it changes. An `npc_id` of zero means
   *  it is not open. */
  onSafebox?: (box: Safebox) => void;
  /** The whole party, whenever any of it changes -- who is in it, what they
   *  are, who is standing with the leader and what the group is currently worth.
   *  A `leader_id` of zero means there is no party. */
  onParty?: (party: PartyFrame) => void;
  /** Somebody has asked this player to group up, or the invitation they were
   *  holding has gone. A `token` of zero is the second of those, and is what
   *  takes the prompt down: answered, declined, expired, or its inviter logged
   *  out. */
  onPartyInvitation?: (invitation: PartyInvitationFrame) => void;
  /** The whole guild, whenever any of it changes, as *this* member sees it --
   *  `your_grade` is the reader's own rank and differs per recipient, which is
   *  why the server does not share one frame between them. A `guild_id` of zero
   *  means there is no guild. */
  onGuild?: (guild: GuildFrame) => void;
  /** The server-owned plot and durable building rows for this member's guild. */
  onGuildLand?: (state: GuildLandStateFrame) => void;
  /** Every guild plot on the map just entered, with its owner
   *  (`CManager::SendLandList`, `building.cpp:945-995`). */
  onLandList?: (list: LandListFrame) => void;
  /** One guild's emblem: 768 samples of 16x12 RGBA the *server* encoded, never
   *  the bytes anybody uploaded (`emblem.canonical_rgba`). An empty `rgba` is
   *  'no emblem', which is also the answer for a guild id that names nothing --
   *  see `RequestEmblem` for why those are deliberately the same frame. */
  onEmblem?: (emblem: EmblemFrame) => void;
  /** One guild's symbol file, sent because this client's copy differed or the
   *  symbol changed (`input_login.cpp:818-844`). */
  onGuildSymbol?: (symbol: GuildSymbolFrame) => void;
  /** Somebody has asked this player into a guild, or the invitation they were
   *  holding has gone. A `token` of zero is the second of those. */
  onGuildInvitation?: (invitation: GuildInvitationFrame) => void;
  /** The server asks this client to open the existing guild-name form. */
  onGuildFounding?: () => void;
  onGuildWarMenu?: (menu: GuildWarMenuFrame) => void;
  onGuildWarArena?: (arena: GuildWarArenaFrame) => void;
  onGuildComments?: (comments: GuildCommentsFrame) => void;
  /** Somebody nearby made a gesture -- this player included, since the server
   *  broadcasts to the actor's own view as well. Both ids are entities the
   *  receiver could see at the moment it was sent; `target` is zero for a
   *  gesture aimed at nobody. */
  onEmotePlayed?: (played: EmotePlayedFrame) => void;
  /** Somebody wants to kiss or slap this player and is asking first, or the
   *  request they were holding has gone. A `token` of zero is the second of
   *  those, and is what takes the prompt down. */
  onEmoteRequest?: (request: EmoteRequestFrame) => void;
  /** This player's own horse, whole, whenever any of it changes. A `level` of
   *  zero means there is none. Nobody else's horse ever arrives here. */
  onMount?: (mount: MountFrame) => void;
  /** The state of this player's line, whenever it changes. A `state` of 2 means
   *  a fish is biting *now* -- it is the frame that starts the reaction window,
   *  and the only warning there is. */
  onFishing?: (line: FishingFrame) => void;
  /** What came of one pull. An event, not a state: see `FishCaught`. */
  onFishCaught?: (caught: FishCaughtFrame) => void;
  /** One step of somebody's fishing act, this player's own included: an event
   *  every client that sees the angler is sent (`fishing.cpp:398-423`). */
  onFishingAct?: (act: FishingActFrame) => void;
  /** This player's own swing, whole, whenever it changes. */
  onMining?: (swing: MiningFrame) => void;
  /** What one swing came to. An event, not a state: see `MineResult`. */
  onMineResult?: (result: MineResultFrame) => void;
  /** The one OX statement currently visible. The bank and correct answer never
   *  arrive here before the round closes. */
  onOXQuestion?: (question: OXQuestionFrame) => void;
  /** The closed round's human-visible verdict. */
  onOXResult?: (result: OXResultFrame) => void;
  /** A server-issued anti-bot question that must stay visible until answered. */
  onAutoBanQuiz?: (quiz: AutoBanQuizFrame) => void;
  /** Where this player stands with respect to dungeons -- the door in front of
   *  them, or the floor they are on. State, so the connection keeps the last
   *  one and a panel built later is told immediately. */
  onDungeon?: (state: DungeonStateFrame) => void;
  /** This player has turned into something, or turned back. An empty `model` is
   *  the second of those. Nobody else's transformation ever arrives here -- a
   *  bystander's copy is `RemoteEntity.polymorph`, which is what the renderer
   *  reads and all it needs. */
  onPolymorphed?: (shape: PolymorphedFrame) => void;
  /** This player's pet is out, or is not. An `entity_id` of zero is the second
   *  of those, and it arrives unprompted as often as it arrives in reply: the
   *  server sends the pet home the moment its summoning item leaves the bag
   *  (`PetSystem.cpp:279-286`), which nobody clicked anything to cause. */
  onPetSummoned?: (pet: PetSummonedFrame) => void;

  onArenaInvitation?: (invitation: ArenaInvitationFrame) => void;
  /** The duel this player is in, or watching, whole -- whenever any of it
   *  changes. A `duel_id` of zero means neither. Symmetric: find yourself by
   *  comparing `a` and `b` against `selfId`. */
  onArenaDuel?: (duel: ArenaDuelFrame) => void;
  /** How one match ended. An event, not a state, so it is not stored -- see
   *  `ArenaResult`. Nothing this client sends can produce one. */
  onArenaResult?: (result: ArenaResultFrame) => void;

  onStallSign?: (entityId: number, sign: string) => void;
  /** The stall this player is browsing, whole, whenever any of it changes. A
   *  `seller_id` of zero means none is open. Also arrives unprompted: a shelf
   *  another buyer just took is a change to a window nobody here clicked. */
  onStall?: (stall: StallWindowFrame) => void;
  /** Something on *this player's own* stall sold. An event, not a state, and
   *  sent to the seller alone -- the `Bag` frame beside it is the money, and
   *  this is who took what. */
  onStallSold?: (sale: StallSoldFrame) => void;

  onTargeted?: (targeted: TargetedFrame) => void;
  /** The target's gear, sent only after an AOI-confirmed `Target`. */
  onEquipmentView?: (view: EquipmentView_) => void;

  /** This player's marriage, whole, whenever any of it changes -- including the
   *  questions addressed to them. A `state` of zero means there is none. There
   *  is no message this client can send that reports any of it. */
  onMarriageStatus?: (status: MarriageStatusFrame) => void;
  /** This player's friend list, whole, with the requests waiting for them.
   *  Arrives on join, when somebody on it signs in or out, when a request is
   *  answered and when either block switch moves. */
  onFriendList?: (list: FriendListFrame) => void;
  /** The read-only answer to `/minfo`: exactly three ruler names, and the
   *  caller's own treasury only when the server says they occupy that throne. */
  onMonarchInfo?: (info: MonarchInfoFrame) => void;

  /** The auction board, whole. Only ever arrives in answer to something this
   *  client sent -- `openAuction`, or its own listing, bid, buy-now or claim
   *  -- which is what lets a panel open on any frame it receives without the
   *  server keeping a record that one is open. */
  onAuctionBoard?: (board: AuctionBoardFrame) => void;
  /** Something happened to a lot this player has money or an item in, while
   *  they were looking elsewhere. An event: it opens nothing, and the board it
   *  describes is fetched with `openAuction` like any other. */
  onAuctionNews?: (news: AuctionNewsFrame) => void;
  /** This account's item mall. Arrives on join and after every checkout, and
   *  is almost always empty -- there is no purchase path in this build. */
  onMall?: (mall: MallWindowFrame) => void;

  /** You are somewhere else now. An event with a moment, like `FishCaught`:
   *  the position is already reconciled into the predictor by the time this
   *  fires, so a sink is for the camera and the sound, not for the move. */
  onWarp?: (warp: WarpFrame) => void;
  /** You have left this map for that one. Carries the map to load and the
   *  token to come back with, and nothing else -- see `MapChange`. */
  onMapChange?: (change: MapChangeFrame) => void;
  /** Somebody in sight has pointed a bow. `RecvFlyTargetingPacket` hands the
   *  same four numbers to the shooter's actor
   *  (`PythonNetworkStreamPhaseGame.cpp:2542-2575`). */
  onAimed?: (aim: AimedFrame) => void;
}

/** Options with the defaults filled in, so nothing downstream re-checks them. */
type ResolvedOptions = ConnectionOptions &
  Required<Pick<ConnectionOptions, "stats" | "build" | "now">>;

/** What the callback named by a `RELAYED` row takes -- the decoded frame. Used
 *  to make a row that pairs the wrong message with the wrong callback a type
 *  error rather than a silent mis-wire. */
type Payload<H extends keyof ResolvedOptions> =
  NonNullable<ResolvedOptions[H]> extends (value: infer P) => void ? P : never;

/** One row of `Connection.RELAYED`. */
type RelayRow = [number, (conn: Connection, reader: Reader) => void];


/** A live session against one server, including its reconnect loop. */
const TARGET_UNACKED = 5;

/** Milliseconds of tick-period correction per input of queue error. */
const PACING_GAIN_MS = 1.5;

/** Cap on that correction, so the rate is nudged rather than lurched. */
const MAX_PACING_SKEW_MS = 8;

/** Bytes per `GuildSymbolChunk`: two wire bytes a sample at worst, so a piece
 *  stays under the server's `MAX_MESSAGE_BYTES` of 1552 (`session.py`). */
export const GUILD_SYMBOL_CHUNK_BYTES = 600;

/** The imported effect a `SpecificEffect` file names: its base name without
 *  `.mse`, lower-cased, which is the key `tools/build_effects.py` imports it under.
 *  `RecvSpecificEffect` registers the effect by that file
 *  (`PythonNetworkStreamPhaseGameItem.cpp:903-906`). */
export function specificEffectKey(effectFile: string): string {
  const base = effectFile.split(/[\\/]/).filter((part) => part !== "").pop() ?? "";
  return base.replace(/\.mse$/i, "").toLowerCase();
}

export class Connection {
  status: ConnectionStatus = "idle";
  predictor: Predictor | null = null;
  readonly entities = new EntityInterpolator();
  private readonly footprints = new Map<
    number,
    readonly [number, number, number, number]
  >();

  private swingLock = new SwingLock();
  /** Where the current swing is aimed, in heading steps, or null when nothing
   *  is being swung at. See `faceTowards`. */
  private swingFacing: number | null = null;

  /** Last measured round trip, in milliseconds. -1 until the first Pong. */
  pingMs = -1;
  /** Local send time for the outstanding ping; never sent on the wire. */
  private pingSentAtMs: number | null = null;
  serverTick = 0;
  ackSeq = -1;
  entityId = -1;
  snapshotsApplied = 0;
  /** The local player's health, which never goes through the interpolator: the
   *  local entity is reconciled, not interpolated, so its delta is consumed on a
   *  different path and its vitals have to be merged on that path too. */
  selfVitals: Vitals = NO_VITALS;

  /** Level and experience, from `Standing`. Sent to this client alone, so it is
   *  the only place the player's own progress is authoritative. */
  standing: Standing_ = NO_STANDING;

  /** The bag, exactly as last sent. Not a merge target -- see `Inventory`. */
  bag: Inventory = NO_BAG;

  souls: DragonSouls = NO_SOULS;

  /** This character's player-versus-player mode, as the server last stated it.
   *  Never set from a request: `setPvpMode` sends and waits, because the whole
   *  reason the server answers every request is that the answer may differ. */
  pvpMode: number = NO_PVP_MODE;

  readonly duels = new Map<string, DuelRecord>();

  /** The spirit pool, exactly as last sent. Like `standing`, never predicted. */
  spirit: Spirit_ = NO_SPIRIT;

  /** The stamina gauge, as last sent and when. */
  stamina: Stamina_ = NO_STAMINA;

  /** The skill bar, exactly as last sent. */
  skills: Skills_ = NO_SKILLS;

  quickslots: readonly QuickSlot_[] = NO_QUICK_SLOTS;

  /** Live timed modifiers, exactly as last sent. */
  affects: readonly Affect_[] = [];

  /** Every open quest, exactly as last sent. Whole, never patched -- the server
   *  resends the lot on any change, so there is nothing here to merge and no way
   *  for this list to drift from the one the server holds. */
  questLog: readonly QuestEntry_[] = [];

  /** The map screen's NPC markers, exactly as last sent (`SendNPCPosition`,
   *  `sectree_manager.cpp:1051-1090`). Held because the frame lands in the
   *  burst after Welcome, before the world screen exists to draw it. */
  npcMarkers: readonly NpcMarker_[] = [];

  /** Every live quest arrow (`target.cpp:15-47`), with its latest position,
   *  held for the same reason as `npcMarkers`. */
  readonly questTargets = new Map<number, Extract<QuestTarget_, { kind: "create" }>>();

  /** The open dealer's counter, exactly as last sent. Whole, never patched, for
   *  the same reason `bag` is: a purchase names a cell in the server's layout,
   *  so a stock list that drifted would be a player buying what they did not
   *  click. */
  shop: ShopStock = NO_SHOP;

  /** The open trade, exactly as last sent. Whole, never patched: an accept
   *  names the server's token for one pair of offers, so a window that had
   *  drifted would be a window agreeing to something else. */
  exchange: ExchangeState = NO_EXCHANGE;

  stall: StallWindowFrame = NO_STALL;

  readonly stallSigns = new Map<number, string>();

  auction: AuctionBoardFrame = NO_AUCTION;

  /** This account's item mall, exactly as last sent. */
  mall: MallWindowFrame = NO_MALL;

  /** The open storeroom, exactly as last sent. */
  safebox: Safebox = NO_SAFEBOX;

  /** The party, exactly as last sent. Whole, never patched: the roster is the
   *  server's and a client that merged into it would be drawing a group the
   *  server does not have. */
  party: PartyFrame = NO_PARTY;

  invitation: PartyInvitationFrame = NO_INVITATION;

  /** This player's line, exactly as last sent. Whole, never patched. */
  fishing: FishingFrame = NO_FISHING;

  /** This player's own swing, exactly as last sent. Whole, never patched, for
   *  the fishing line's reason: the server owns the clock and a client that
   *  merged fields into a swing of its own would be counting down an interval
   *  the server is not measuring. */
  mining: MiningFrame = NO_MINING;

  /** Where this player stands with respect to dungeons, exactly as last sent.
   *  Whole, never patched, for the swing's reason. */
  dungeon: DungeonStateFrame = NO_DUNGEON;

  /** The last light mode the shard actually sent. Reset on Welcome so a
   * reconnect cannot retain the old world's darkness before its state frame. */
  environmentNight = false;
  weather = 0;
  /** Last map/event BGM state; reset before Welcome rebuilds a world. */
  fieldMusic = { track: 0, volume_percent: 100 };

  /** Which map this shard is serving, from the Welcome. Held so a `Warp`
   *  naming another one can be refused rather than drawn on the wrong ground. */
  mapId: string = "";

  /** This player's own horse, exactly as last sent. Whole, never patched, for
   *  the party roster's reason: a client that merged fields into a horse of its
   *  own would be drawing a horse the server does not have. */
  mount: MountFrame = NO_MOUNT;

  /** What this player is currently shaped like, exactly as last sent. Whole,
   *  never patched, for `mount`'s reason. */
  polymorphed: PolymorphedFrame = NOT_POLYMORPHED;

  /** This player's own pet, exactly as last sent. Never anybody else's: a
   *  stranger's pet is an ordinary entity in the snapshot, flagged
   *  `ENTITY_PET`, and this client has no business knowing who owns it. */
  pet: PetSummonedFrame = NO_PET;

  emoteRequest: EmoteRequestFrame = NO_EMOTE_REQUEST;

  /** The duel challenge currently standing, exactly as last sent. Read by the
   *  panel to *draw* from; the token it answers with is the one it drew, never
   *  this. See `arenaAnswer`. */
  arenaInvitation: ArenaInvitationFrame = NO_ARENA_INVITATION;

  /** The duel this client is in or watching, exactly as last sent. Whole,
   *  never patched -- the score on it is the server's and nothing here
   *  increments it. */
  arenaDuel: ArenaDuelFrame = NO_ARENA_DUEL;

  targeted: TargetedFrame = NO_TARGETED;

  readonly partyTargets = new Map<number, TargetedFrame>();

  /** The guild, exactly as last sent. Whole, never patched, for the roster's
   *  reason above -- and per recipient, so `your_grade` is this client's own. */
  guild: GuildFrame = NO_GUILD;
  /** The guild's durable plot/buildings, exactly as last sent. */
  guildLand: GuildLandStateFrame = new GuildLandStateFrame();
  /** The plots of the map last entered, exactly as last sent. */
  landList: LandListFrame = new LandListFrame();

  readonly guildNames = new Map<number, string>();
  /** Each ground item in view: the vnum `TPacketGCItemGroundAdd` names and when
   *  it arrived, which starts the drop's fall (`PythonItem.cpp:298-445`). */
  readonly groundItems = new Map<number, { readonly vnum: number; readonly seenMs: number }>();

  /** This player's marriage, exactly as last sent. Whole, never patched: the
   *  score on it is `GetMarriagePoint`'s and nothing here recomputes it. The
   *  tokens a panel answers with are the ones it *drew* from a frame, never
   *  read off this at click time -- see `answerMarriage`. */
  marriage: MarriageStatusFrame = NO_MARRIAGE;

  /** The friend list, exactly as last sent. Whole, for `Guild`'s reason. */
  friends: FriendListFrame = NO_FRIENDS;

  /** The guild invitation currently outstanding, or one with a zero token. Read
   *  by the panel to *draw*; the token it answers with is the one it drew, never
   *  this. See `guildAnswer`. */
  guildInvitation: GuildInvitationFrame = NO_GUILD_INVITATION;
  guildWarMenu: GuildWarMenuFrame = new GuildWarMenuFrame();
  guildWarArena: GuildWarArenaFrame = new GuildWarArenaFrame();
  guildComments: GuildCommentsFrame = new GuildCommentsFrame();

  /** How long a dead player waits, in server ticks. From the Welcome, so a
   *  server that changes it does not need a client release. */
  respawnTicks = 0;

  characters: CharactersFrame | null = null;

  /** Set once a snapshot has actually reported this character's health. */
  selfRace = 0;

  selfMount = 0;

  /** The local player's public entity flags from their own snapshot. */
  selfFlags = 0;
  /** `EntityDelta.affect_flags` off this character's own snapshot. */
  selfAffectFlags = 0;

  /** This character's own operator level, off its own snapshot -- the same
   *  field every other client is told about this body, so the mark a GM sees
   *  over their own head is the one everybody else sees over it. */
  selfGmLevel = 0;

  private sawHealth = false;

  get dead(): boolean {
    return this.sawHealth && this.selfVitals.hp <= 0;
  }

  selfName = "";

  private readonly options: ResolvedOptions;
  /** The server's last word on the local player, before any prediction is layered
   *  on top. Deltas merge onto this, not onto what the client currently draws. */
  private authoritative: MoveState | null = null;
  private socket: WebSocket | null = null;
  private tickTimer: ReturnType<typeof setTimeout> | null = null;
  private pacingMs = TICK_MS;
  /** Wall-clock accounting for the input timer. Rebuilt whenever ticking starts,
   *  so a socket that was down for a minute does not come back owing a minute of
   *  movement. */
  private clock = new InputClock(0);
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private closing = false;
  private crossed = false;
  private readonly startedAtMs: number;

  private walkModeOn = false;

  constructor(options: ConnectionOptions) {
    this.options = {
      stats: DEFAULT_STATS,
      build: "web",
      now: () => Date.now(),
      ...options,
    };
    this.startedAtMs = this.options.now();
  }

  // -------------------------------------------------------------- lifecycle

  open(): void {
    this.closing = false;
    this.dial();
  }

  close(): void {
    this.closing = true;
    this.stopTimers();
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.socket?.close();
    this.socket = null;
    this.setStatus("idle", "closed");
  }

  private dial(): void {
    this.setStatus("connecting", this.options.url);
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.options.url);
    } catch (error) {
      this.scheduleReconnect(String(error));
      return;
    }
    socket.binaryType = "arraybuffer";
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus("handshaking", "hello sent");

      const hello = new Hello();
      hello.protocol_version = PROTOCOL_VERSION;
      hello.schema_hash = SCHEMA_HASH;
      hello.build = this.options.build;
      this.send(hello.encode());

      const crossing = this.options.crossing;
      if (crossing) {
        // Before both of the others, because a crossing has already been
        // authenticated: `Login` here would sign the account in on the *boot*
        // map, spawning the character in town while the one that walked off
        // the edge sat in the crossing table until it expired.
        const arrive = new Arrive();
        arrive.token = crossing;
        this.send(arrive.encode());
        return;
      }

      const registration = this.options.registration;
      if (registration) {
        // A sign-up connection. One frame, and deliberately neither of the two
        // below: see `ConnectionOptions.registration`.
        this.register(registration.username, registration.password);
        return;
      }

      const credentials = this.options.credentials;
      if (credentials) {
        const login = new Login();
        login.username = credentials.username;
        login.password = credentials.password;
        this.send(login.encode());
      } else {
        const join = new Join();
        join.name = this.options.playerName;
        this.send(join.encode());
      }
    };

    socket.onmessage = (event: MessageEvent) => {
      // Text frames are not part of this protocol; ignoring them keeps a proxy's
      // stray keepalive from tearing the session down.
      if (!(event.data instanceof ArrayBuffer)) return;
      try {
        this.handle(new Uint8Array(event.data));
      } catch (error) {
        // A frame we cannot parse means the stream is no longer trustworthy: the
        // reader's position is wherever the failure left it. Drop the connection
        // and resynchronise from a fresh handshake.
        this.setStatus("connecting", `decode failed: ${String(error)}`);
        socket.close();
      }
    };

    socket.onclose = (event: CloseEvent) => {
      this.stopTimers();
      if (this.status === "rejected") return;
      if (this.closing) return;
      if (this.options.registration) {
        this.setStatus(
          "offline",
          `registration socket closed (code ${event.code}${
            event.reason ? `: ${event.reason}` : ""
          })`,
        );
        return;
      }
      this.scheduleReconnect("socket closed");
    };

    socket.onerror = () => {
      // `onclose` always follows, and carries the reconnect. Nothing to do here
      // beyond not throwing out of an event handler.
    };
  }

  private scheduleReconnect(reason: string): void {
    this.socket = null;
    if (this.closing) return;
    if (this.crossed) {
      // A crossing is in progress and this socket closing is the *expected*
      // half of it. Reconnecting would sign back in on the map that was left.
      this.setStatus("offline", "crossing to another map");
      return;
    }

    const backoff = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.attempt);
    // Full jitter: without it, everyone knocked off by one restart returns
    // together and knocks the server over again.
    const delay = Math.round(backoff * (0.5 + Math.random() * 0.5));
    this.attempt += 1;
    this.setStatus("offline", `${reason}; retrying in ${delay} ms`);
    this.reconnectTimer = setTimeout(() => this.dial(), delay);
  }

  private stopTimers(): void {
    if (this.tickTimer !== null) clearTimeout(this.tickTimer);
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.tickTimer = null;
    this.pingTimer = null;
  }

  private setStatus(status: ConnectionStatus, detail: string): void {
    this.status = status;
    this.options.onStatus?.(status, detail);
  }

  private send(frame: Uint8Array): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(frame);
  }

  // ---------------------------------------------------------------- inbound

  /** A `RELAYED` row that decodes the frame straight into the callback. */
  private static relay<H extends keyof ResolvedOptions>(
    opcode: number,
    codec: { decode(reader: Reader): Payload<H> },
    hook: H,
  ): RelayRow {
    return [
      opcode,
      (conn, reader) => {
        const handler = conn.options[hook] as ((value: Payload<H>) => void) | undefined;
        handler?.(codec.decode(reader));
      },
    ];
  }

  /** A `RELAYED` row that also remembers the frame on `field`, for the panels
   *  that re-read it when they are opened or after a reconnect. */
  private static keep<H extends keyof ResolvedOptions, F extends keyof Connection>(
    opcode: number,
    codec: { decode(reader: Reader): Payload<H> & Connection[F] },
    field: F,
    hook: H,
  ): RelayRow {
    return [
      opcode,
      (conn, reader) => {
        const value = codec.decode(reader);
        conn[field] = value;
        const handler = conn.options[hook] as ((value: Payload<H>) => void) | undefined;
        handler?.(value);
      },
    ];
  }

  private static readonly RELAYED: ReadonlyMap<
    number,
    (conn: Connection, reader: Reader) => void
  > = new Map([
    // Decode and forward.
    Connection.relay(Opcode.StallSold, StallSoldFrame, "onStallSold"),
    Connection.relay(Opcode.AuctionNews, AuctionNewsFrame, "onAuctionNews"),
    Connection.relay(Opcode.RefineOffer, RefineOffer, "onRefineOffer"),
    Connection.relay(Opcode.CubeRecipes, CubeRecipes, "onCubeRecipes"),
    Connection.relay(Opcode.CubeResult, CubeResult, "onCubeResult"),
    Connection.relay(Opcode.CubeClosed, CubeClosed, "onCubeClosed"),
    Connection.relay(Opcode.DragonSoulRefined, DragonSoulRefined, "onDragonSoulRefined"),
    Connection.relay(Opcode.RefineResult, RefineResult, "onRefineResult"),
    Connection.relay(Opcode.Emblem, EmblemFrame, "onEmblem"),
    Connection.relay(Opcode.GuildSymbol, GuildSymbolFrame, "onGuildSymbol"),
    Connection.relay(Opcode.OXQuestion, OXQuestionFrame, "onOXQuestion"),
    Connection.relay(Opcode.OXResult, OXResultFrame, "onOXResult"),
    Connection.relay(Opcode.AutoBanQuiz, AutoBanQuizFrame, "onAutoBanQuiz"),
    // Decode, remember, forward.
    Connection.keep(Opcode.StallWindow, StallWindowFrame, "stall", "onStall"),
    Connection.keep(Opcode.MarriageStatus, MarriageStatusFrame, "marriage", "onMarriageStatus"),
    Connection.keep(Opcode.FriendList, FriendListFrame, "friends", "onFriendList"),
    Connection.keep(Opcode.AuctionBoard, AuctionBoardFrame, "auction", "onAuctionBoard"),
    Connection.keep(Opcode.MallWindow, MallWindowFrame, "mall", "onMall"),
    Connection.keep(Opcode.DragonSouls, DragonSouls, "souls", "onDragonSouls"),
    Connection.keep(Opcode.ExchangeState, ExchangeState, "exchange", "onExchange"),
    Connection.keep(Opcode.Safebox, Safebox, "safebox", "onSafebox"),
    Connection.keep(Opcode.Party, PartyFrame, "party", "onParty"),
    Connection.keep(Opcode.Guild, GuildFrame, "guild", "onGuild"),
    Connection.keep(Opcode.GuildLandState, GuildLandStateFrame, "guildLand", "onGuildLand"),
    Connection.keep(Opcode.LandList, LandListFrame, "landList", "onLandList"),
    Connection.keep(Opcode.GuildInvitation, GuildInvitationFrame, "guildInvitation", "onGuildInvitation"),
    Connection.keep(Opcode.GuildWarMenu, GuildWarMenuFrame, "guildWarMenu", "onGuildWarMenu"),
    Connection.keep(Opcode.GuildWarArena, GuildWarArenaFrame, "guildWarArena", "onGuildWarArena"),
    Connection.keep(Opcode.GuildComments, GuildCommentsFrame, "guildComments", "onGuildComments"),
    Connection.keep(Opcode.PartyInvitation, PartyInvitationFrame, "invitation", "onPartyInvitation"),
    Connection.keep(Opcode.EmoteRequest, EmoteRequestFrame, "emoteRequest", "onEmoteRequest"),
    Connection.keep(Opcode.ArenaInvitation, ArenaInvitationFrame, "arenaInvitation", "onArenaInvitation"),
    Connection.keep(Opcode.ArenaDuel, ArenaDuelFrame, "arenaDuel", "onArenaDuel"),
    Connection.keep(Opcode.Fishing, FishingFrame, "fishing", "onFishing"),
    Connection.keep(Opcode.Mining, MiningFrame, "mining", "onMining"),
    Connection.keep(Opcode.DungeonState, DungeonStateFrame, "dungeon", "onDungeon"),
    Connection.keep(Opcode.Polymorphed, PolymorphedFrame, "polymorphed", "onPolymorphed"),
    Connection.keep(Opcode.PetSummoned, PetSummonedFrame, "pet", "onPetSummoned"),
  ]);

  /** Dispatch one frame. Exposed for tests, which drive it without a socket. */
  handle(frame: Uint8Array): void {
    const reader = new Reader(frame);
    const opcode = reader.u8();

    // Thirty-six opcodes are nothing but a decode and a callback; they live in
    // `RELAYED` rather than as thirty-six identical paragraphs below.
    const relayed = Connection.RELAYED.get(opcode);
    if (relayed !== undefined) {
      relayed(this, reader);
      return;
    }

    switch (opcode) {
      case Opcode.Welcome:
        this.onWelcome(Welcome.decode(reader));
        return;
      case Opcode.Reject: {
        const reject = Reject.decode(reader);
        // A rejection is a decision, not a failure: retrying a version mismatch
        // in a loop tells the player nothing and hammers the server.
        this.closing = true;
        this.setStatus("rejected", `${reject.code}: ${reject.reason}`);
        this.socket?.close();
        return;
      }
      case Opcode.Snapshot:
        this.onSnapshot(Snapshot.decode(reader));
        return;
      case Opcode.Environment:
        this.environmentNight = Environment.decode(reader).night;
        this.options.onEnvironment?.(this.environmentNight);
        return;
      case Opcode.Weather:
        this.weather = Weather.decode(reader).kind;
        this.options.onWeather?.(this.weather);
        return;
      case Opcode.FieldMusic:
        this.fieldMusic = FieldMusic.decode(reader);
        this.options.onFieldMusic?.(
          this.fieldMusic.track,
          this.fieldMusic.volume_percent,
        );
        return;
      case Opcode.ChatEcho: {
        const chat = ChatEcho.decode(reader);
        if (emoticons.register(chat.entity_id, chat.text, this.options.now())) return;
        // `GetInstancePtr(dwVID)` NULL drops the whole packet before either
        // effect below runs (`PythonNetworkStreamPhaseGame.cpp:1283-1285`).
        // The local player is reconciled, not entered into `entities`
        // (`onSnapshot`), so it is its own case rather than a lookup miss.
        const isSelf = chat.entity_id === this.entityId;
        const speaker = chat.entity_id === 0 || isSelf ? null : this.entities.get(chat.entity_id);
        if (chat.entity_id !== 0 && !isSelf && speaker === null) return;
        if (chat.channel !== ChatChannel.SHOUT) {
          chatTails.register(chat.entity_id, chat.text);
        }
        // `AppendChat` into the chat window runs only `if
        // (pkInstChatter->IsPC())` (`:1345-1346`); a monster or NPC's line
        // reaches the player only as the head bubble registered above.
        // entity_id 0 is the no-VID branch (a quest or system line), which
        // always reaches the window (`:1348-1369`).
        if (chat.entity_id !== 0 && !isSelf && speaker!.kind !== EntityKind.PLAYER) return;
        this.options.onChat?.(chat.name, chat.text, chat.channel);
        return;
      }
      case Opcode.Whispered: {
        const whisper = Whispered.decode(reader);
        // Its own callback rather than `onChat` with `ChatChannel.WHISPER`,
        // because the frame genuinely has less on it: there is no entity id and
        // there never will be. Collapsing the two here would mean inventing a
        // zero for a field this message does not have.
        this.options.onWhisper?.(whisper.from_name, whisper.text, whisper.refusal);
        return;
      }
      case Opcode.CombatEvent: {
        const event = CombatEvent.decode(reader);
        this.options.onCombat?.({
          attackerId: event.attacker_id,
          targetId: event.target_id,
          damage: event.damage,
          flags: event.flags,
          skill: event.skill,
          comboMotion: event.combo_motion,
        });
        return;
      }
      case Opcode.SyncPosition: {
        // `RecvSyncPositionPacket` -> `SyncActor` -> `NEW_SyncPixelPosition`
        // (`PythonNetworkStreamPhaseGameActor.cpp:390-411`, `NetworkActorManager.cpp:539-557`).
        const sync = SyncPosition.decode(reader);
        const blocked = (xMm: number, yMm: number): boolean => this.options.world.isBlocked(xMm, yMm);
        const self = sync.entity_id === this.entityId ? this.predictor?.state ?? null : null;
        // The local body's position is the predictor's to reconcile; only the
        // shove's motion is answered for it here.
        const shove = self !== null
          ? pushOutcome({
            fromXMm: self.xMm, fromYMm: self.yMm, toXMm: sync.x_mm, toYMm: sync.y_mm,
            resistsFall: false, usingSkill: this.options.usingSkill?.(sync.entity_id) ?? false, blocked,
          })
          : this.entities.push(sync.entity_id, sync.x_mm, sync.y_mm, blocked,
            this.options.usingSkill?.(sync.entity_id) ?? false);
        if (shove !== null) this.options.onSyncPosition?.(sync.entity_id, shove);
        return;
      }
      case Opcode.CreateFly: {
        const fly = CreateFly.decode(reader);
        this.options.onCreateFly?.({
          fly: fly.fly,
          fromId: fly.from_id,
          toId: fly.to_id,
        });
        return;
      }
      case Opcode.Standing: {
        const standing = Standing.decode(reader);
        this.standing = {
          level: standing.level,
          exp: standing.exp,
          expToNext: standing.exp_to_next,
          maxHp: standing.max_hp,
          attackMin: standing.attack_min,
          attackMax: standing.attack_max,
          defence: standing.defence,
          str: standing.stat_str,
          dex: standing.stat_dex,
          int: standing.stat_int,
          con: standing.stat_con,
          points: standing.stat_points,
          attackSpeed: standing.attack_speed,
          moveSpeed: standing.move_speed,
          energy: standing.energy,
          maxEnergy: standing.max_energy,
          statResetCount: standing.stat_reset_count,
          castingSpeed: standing.casting_speed,
          magicAttackMin: standing.magic_attack_min,
          magicAttackMax: standing.magic_attack_max,
          magicDefence: standing.magic_defence,
          evadeRate: standing.evade_rate,
          weaponMin: standing.weapon_min,
          weaponMax: standing.weapon_max,
          weaponMagicMin: standing.weapon_magic_min,
          weaponMagicMax: standing.weapon_magic_max,
        };
        this.options.onStanding?.(this.standing);
        this.selfVitals = {
          ...this.selfVitals,
          level: standing.level,
          maxHp: this.sawHealth ? standing.max_hp : this.selfVitals.maxHp,
        };
        if (standing.gained > 0) this.options.onLevel?.(standing.level, standing.gained);
        return;
      }
      case Opcode.Bag: {
        const bag = BagFrame.decode(reader);
        this.bag = {
          gold: bag.gold,
          capacity: bag.capacity,
          slots: bag.slots.map(bagSlotOf),
          worn: bag.worn.map(bagSlotOf),
        };
        this.options.onBag?.(this.bag);
        return;
      }
      case Opcode.ItemSet: {
        const cell = ItemSetFrame.decode(reader);
        this.bag = withCell(this.bag, cell.window, bagSlotOf(cell));
        this.options.onBag?.(this.bag);
        return;
      }
      case Opcode.ItemDel: {
        const gone = ItemDelFrame.decode(reader);
        this.bag = withoutCell(this.bag, gone.window, gone.slot);
        this.options.onBag?.(this.bag);
        return;
      }
      case Opcode.Gold: {
        this.bag = { ...this.bag, gold: GoldFrame.decode(reader).gold };
        this.options.onBag?.(this.bag);
        return;
      }
      case Opcode.StatusResetOffer: {
        StatusResetOffer.decode(reader);
        this.options.onStatusResetOffer?.();
        return;
      }
      case Opcode.StatusResetConfirm: {
        StatusResetConfirm.decode(reader);
        this.options.onStatusResetConfirm?.();
        return;
      }
      case Opcode.Shop: {
        const frame = ShopFrame.decode(reader);
        this.shop = {
          npcId: frame.npc_id,
          name: frame.name,
          items: frame.items.map((i) => ({
            slot: i.slot,
            vnum: i.vnum,
            count: i.count,
            name: i.name,
            kind: i.kind,
            price: i.price,
            levelLimit: i.level_limit,
            attackMin: i.attack_min,
            attackMax: i.attack_max,
            defence: i.defence,
            healHp: i.heal_hp,
            healHpPct: i.heal_hp_pct,
            barredJobs: i.barred_jobs,
          })),
        };
        this.options.onShop?.(this.shop);
        return;
      }
      // ---- a player's own stall
      case Opcode.StallSign: {
        const sign = StallSignFrame.decode(reader);
        if (sign.sign === "") this.stallSigns.delete(sign.entity_id);
        else this.stallSigns.set(sign.entity_id, sign.sign);
        this.options.onStallSign?.(sign.entity_id, sign.sign);
        return;
      }
      case Opcode.MonarchInfo: {
        // A response event, not login state: `/minfo` is the only trigger and
        // closing the panel must not make a reconnect reopen it.
        this.options.onMonarchInfo?.(MonarchInfoFrame.decode(reader));
        return;
      }
      case Opcode.Characters: {
        const characters = CharactersFrame.decode(reader);
        this.characters = characters;
        this.options.onCharacters?.(characters);
        return;
      }
      case Opcode.EmpireSelectPrompt: {
        this.options.onEmpireSelect?.(EmpireSelectPrompt.decode(reader));
        return;
      }
      // ---- character select, delete and registration
      case Opcode.Registered: {
        const answer = Registered.decode(reader);
        this.options.onRegistered?.(answer.ok, answer.username, answer.text);
        return;
      }
      case Opcode.GuildName: {
        // Straight into the name map, which is all `CPythonGuild` does with it
        // (`PythonNetworkStreamPhaseGame.cpp:3604-3608`). No callback: nothing
        // in the UI reacts to a guild being named, the renderer simply reads
        // the map on the next frame.
        const named = GuildNameFrame.decode(reader);
        this.guildNames.set(named.guild_id, named.name);
        return;
      }
      case Opcode.GuildFoundingPrompt: {
        GuildFoundingPrompt.decode(reader);
        this.options.onGuildFounding?.();
        return;
      }
      case Opcode.EmotePlayed: {
        this.options.onEmotePlayed?.(EmotePlayedFrame.decode(reader));
        return;
      }
      case Opcode.Aimed: {
        this.options.onAimed?.(AimedFrame.decode(reader));
        return;
      }

      case Opcode.Targeted: {
        const targeted = TargetedFrame.decode(reader);
        if (targeted.who === this.entityId) {
          this.targeted = targeted;
        } else if (targeted.target === 0) {
          // Deleted rather than stored with a zero, so that "is this member
          // pointing at anything" is `has` rather than a field comparison
          // every caller has to remember to make.
          this.partyTargets.delete(targeted.who);
        } else {
          this.partyTargets.set(targeted.who, targeted);
        }
        this.options.onTargeted?.(targeted);
        return;
      }
      case Opcode.EquipmentView: {
        const frame = EquipmentViewFrame.decode(reader);
        // Reuse the one `BagSlot` mapping: an inspected worn row has the exact
        // same tooltip facts as one's own worn row, and copying the conversion
        // here would make the two equipment windows quietly diverge.
        this.options.onEquipmentView?.({
          entityId: frame.entity_id,
          name: frame.name,
          worn: frame.worn.map(bagSlotOf),
        });
        return;
      }
      case Opcode.ArenaResult: {
        // Not stored, for `EmotePlayed`'s reason: a verdict is an event with a
        // moment, and "the last result" kept as state is something whatever
        // draws it has to invent an end for. The `ArenaDuel` that follows it
        // is the state change.
        this.options.onArenaResult?.(ArenaResultFrame.decode(reader));
        return;
      }
      case Opcode.FishCaught: {
        // Not stored, for `EmotePlayed`'s reason: a catch is an event with a
        // moment, and "the last fish" kept as state is something whatever draws
        // it has to invent an end for.
        this.options.onFishCaught?.(FishCaughtFrame.decode(reader));
        return;
      }
      case Opcode.FishingAct: {
        this.options.onFishingAct?.(FishingActFrame.decode(reader));
        return;
      }
      case Opcode.MineResult: {
        // Not stored, for `FishCaught`'s reason: a result is an event with a
        // moment, and "the last swing" kept as state is something whatever
        // draws it has to invent an end for.
        this.options.onMineResult?.(MineResultFrame.decode(reader));
        return;
      }
      case Opcode.Warp: {
        const warp = WarpFrame.decode(reader);
        // Ground this client has not loaded. There is no second process to
        // reconnect to yet (`metin3/server/warp.py`), so the server never sends
        // this -- and drawing it anyway would put the player on the wrong
        // terrain at coordinates that look perfectly reasonable.
        if (warp.map_id !== this.mapId) return;
        this.entities.forget();
        this.targeted = NO_TARGETED;
        // Not interpolated. A 600 m jump between two snapshots is
        // indistinguishable from lag; `reconcile` sees the distance, clears
        // every pending input and cuts to the new position -- which is exactly
        // what its `teleported` branch already exists for.
        this.authoritative = moveState(warp.x_mm, warp.y_mm);
        this.predictor?.reconcile(this.authoritative, this.ackSeq);
        this.options.onWarp?.(warp);
        return;
      }
      case Opcode.MapChange: {
        const change = MapChangeFrame.decode(reader);
        this.crossed = true;
        this.options.onMapChange?.(change);
        return;
      }
      case Opcode.Mount: {
        this.mount = MountFrame.decode(reader);
        if (this.predictor !== null) {
          this.predictor.stats = {
            ...this.predictor.stats,
            walkMmS: this.mount.walk_mm_s,
            runMmS: this.mount.run_mm_s,
          };
        }
        this.options.onMount?.(this.mount);
        return;
      }
      case Opcode.WalkMode: {
        this.walkModeOn = WalkModeFrame.decode(reader).walking;
        return;
      }
      case Opcode.Spirit: {
        const spirit = SpiritFrame.decode(reader);
        this.spirit = {
          sp: spirit.sp,
          maxSp: spirit.max_sp,
          hpRecovery: spirit.hp_recovery,
          spRecovery: spirit.sp_recovery,
        };
        this.options.onSpirit?.(this.spirit);
        return;
      }
      case Opcode.Stamina: {
        const frame = StaminaFrame.decode(reader);
        this.stamina = {
          stamina: frame.stamina,
          maxStamina: frame.max_stamina,
          consumePerSec: frame.consume_per_sec,
          atMs: performance.now(),
        };
        return;
      }
      case Opcode.Skills: {
        const frame = SkillsFrame.decode(reader);
        this.skills = {
          points: frame.points,
          horsePoints: frame.horse_points,
          slots: frame.slots.map((s) => ({
            slot: s.slot,
            vnum: s.vnum,
            level: s.level,
            maxLevel: s.max_level,
            spCost: s.sp_cost,
            cooldownTicks: s.cooldown_ticks,
            cooldownLeft: s.cooldown_left,
            name: s.name,
            icon: s.icon,
            flags: s.flags,
          })),
        };
        this.options.onSkills?.(this.skills);
        return;
      }
      case Opcode.QuickSlots: {
        const frame = QuickSlotsFrame.decode(reader);
        this.quickslots = frame.slots.map((entry) => ({
          pos: entry.pos,
          type: entry.type,
          target: entry.target,
        }));
        this.options.onQuickSlots?.(this.quickslots);
        return;
      }
      case Opcode.Affects: {
        const frame = AffectsFrame.decode(reader);
        this.affects = frame.entries.map((a) => ({
          source: a.source,
          apply: a.apply,
          value: a.value,
          remaining: a.remaining,
        }));
        this.options.onAffects?.(this.affects);
        return;
      }
      case Opcode.SpecialEffect: {
        const frame = SpecialEffectFrame.decode(reader);
        this.options.onSpecialEffect?.(frame.entity_id, frame.effect);
        return;
      }
      case Opcode.SpecificEffect: {
        const frame = SpecificEffectFrame.decode(reader);
        this.options.onSpecificEffect?.(frame.entity_id, frame.effect_file);
        return;
      }
      case Opcode.PointChange: {
        const frame = PointChangeFrame.decode(reader);
        this.options.onPointChange?.(frame.entity_id, frame.point, frame.value);
        return;
      }
      case Opcode.PvpStatus: {
        const status = PvpStatus.decode(reader);
        this.pvpMode = status.mode;
        this.options.onPvpMode?.(status.mode);
        return;
      }
      case Opcode.PvpDuel: {
        const frame = PvpDuel.decode(reader);
        applyDuelFrame(this.duels, frame.src_id, frame.dst_id, frame.mode);
        return;
      }
      case Opcode.QuestDialog: {
        const frame = QuestDialogFrame.decode(reader);
        this.options.onQuestDialog?.({
          token: frame.token,
          title: frame.title,
          final: frame.final,
          lines: frame.lines.map((l) => ({
            kind: l.kind,
            itemVnum: l.item_vnum,
            text: l.text,
          })),
          choices: frame.choices.map((c) => c.text),
          skin: frame.skin,
        });
        return;
      }
      case Opcode.QuestInputPrompt: {
        const frame = QuestInputPromptFrame.decode(reader);
        this.options.onQuestInput?.({
          token: frame.token,
          title: frame.title,
          maxBytes: frame.max_bytes,
          lines: frame.lines.map((line) => ({
            kind: line.kind,
            itemVnum: line.item_vnum,
            text: line.text,
          })),
        });
        return;
      }
      case Opcode.QuestConfirmPrompt: {
        const frame = QuestConfirmPromptFrame.decode(reader);
        this.options.onQuestConfirm?.({
          token: frame.token,
          text: frame.text,
          remainingMs: frame.remaining_ms,
          waiting: frame.waiting,
        });
        return;
      }
      case Opcode.QuestItemPrompt: {
        const frame = QuestItemPromptFrame.decode(reader);
        this.options.onQuestItem?.({ token: frame.token, cells: frame.cells });
        return;
      }
      case Opcode.QuestLog: {
        const frame = QuestLogFrame.decode(reader);
        this.questLog = frame.entries.map((e) => ({
          index: e.index,
          title: e.title,
          counterName: e.counter_name,
          counter: e.counter,
          clockName: e.clock_name,
          clockSeconds: e.clock_seconds,
          targetId: e.target_id,
          targetDesc: e.target_desc,
        }));
        this.options.onQuestLog?.(this.questLog);
        return;
      }
      case Opcode.QuestTargetCreate: {
        const frame = QuestTargetCreate.decode(reader);
        const created = { kind: "create", id: frame.target_id, xMm: frame.x_mm, yMm: frame.y_mm, description: frame.description } as const;
        this.questTargets.set(created.id, created);
        this.options.onQuestTarget?.(created);
        return;
      }
      case Opcode.QuestTargetUpdate: {
        const frame = QuestTargetUpdate.decode(reader);
        const held = this.questTargets.get(frame.target_id);
        if (held !== undefined) this.questTargets.set(held.id, { ...held, xMm: frame.x_mm, yMm: frame.y_mm });
        this.options.onQuestTarget?.({ kind: "update", id: frame.target_id, xMm: frame.x_mm, yMm: frame.y_mm });
        return;
      }
      case Opcode.QuestTargetDelete: {
        const frame = QuestTargetDelete.decode(reader);
        this.questTargets.delete(frame.target_id);
        this.options.onQuestTarget?.({ kind: "delete", id: frame.target_id });
        return;
      }
      case Opcode.NpcPositions: {
        const frame = NpcPositions.decode(reader);
        this.npcMarkers = frame.markers.map((m) => ({
          kind: m.kind, name: m.name, xMm: m.x_mm, yMm: m.y_mm,
        }));
        this.options.onNpcPositions?.(this.npcMarkers);
        return;
      }
      case Opcode.Notice: {
        const notice = NoticeFrame.decode(reader);
        this.options.onNotice?.(notice.code, notice.text, notice.big);
        return;
      }
      case Opcode.Pong: {
        const pong = Pong.decode(reader);
        if (this.pingSentAtMs !== null) {
          this.pingMs = Math.max(0, this.elapsed() - this.pingSentAtMs);
          this.pingSentAtMs = null;
        }
        this.serverTick = pong.server_tick;
        return;
      }
      default:
        // Unknown opcodes are skipped, not fatal: a newer server may send a
        // message this build has never heard of, and the schema hash already
        // guarantees the ones we do know have not changed shape.
        return;
    }
  }

  private onWelcome(welcome: Welcome): void {
    this.entityId = welcome.entity_id;
    this.respawnTicks = welcome.respawn_ticks;
    this.mapId = welcome.map_id;
    this.entities.forget();
    // The overlay belongs to the map that was just left. A Welcome is a new
    // world -- possibly a different map entirely -- and a carried-over
    // rectangle would be a wall standing where the server has none, which is
    // the one direction this bit must never fail in.
    this.footprints.clear();
    this.restampObjectOverlay();
    this.selfVitals = NO_VITALS;
    this.selfRace = 0;
    this.sawHealth = false;
    this.standing = NO_STANDING;
    this.spirit = NO_SPIRIT;
    this.stamina = NO_STAMINA;
    this.bag = NO_BAG;
    this.souls = NO_SOULS;
    this.pvpMode = NO_PVP_MODE;
    this.duels.clear();
    this.mount = NO_MOUNT;
    // The server re-sends both on every entry (`input_login.cpp:548` and the
    // login `when letter` targets); what the last world held is not this one's.
    this.npcMarkers = [];
    this.questTargets.clear();
    // Back to off on every `Welcome`, for `mount`'s reason just above: a
    // reconnect is a new `Session` and `Session.walking` does not survive one
    // server-side either. The fresh `WalkMode` the server sends after any
    // later `/set_walk_mode` corrects this the same round trip it always did.
    this.walkModeOn = false;
    // Cleared on a fresh `Welcome` for the reason above it: the server rebuilds
    // both from nothing on a reconnect, and a stale shape would leave the panel
    // counting down a transformation that ended while the socket was down.
    this.polymorphed = NOT_POLYMORPHED;
    this.pet = NO_PET;
    this.fishing = NO_FISHING;
    // And the swing, for the line's reason: a swing is runtime state keyed by
    // entity on the server and does not survive a reconnect, so a carried-over
    // one would be a pick in the air that nothing is going to bring down.
    this.mining = NO_MINING;
    this.dungeon = NO_DUNGEON;
    this.environmentNight = false;
    this.weather = 0;
    this.fieldMusic = { track: 0, volume_percent: 100 };
    this.emoteRequest = NO_EMOTE_REQUEST;
    // Both cleared for the same reason as the two above: the server rebuilds
    // its arena state from nothing on a reconnect -- a duel ends the instant a
    // connection drops (`arena.cpp:947-965`) -- so a carried-over scoreboard
    // would be a match nobody is fighting.
    this.arenaInvitation = NO_ARENA_INVITATION;
    this.arenaDuel = NO_ARENA_DUEL;
    this.marriage = NO_MARRIAGE;
    this.friends = NO_FRIENDS;
    this.stall = NO_STALL;
    this.stallSigns.clear();
    chatTails.clear();
    // And the emoticon clock, for the same reason and by the same rule.
    emoticons.clear();
    this.targeted = NO_TARGETED;
    this.partyTargets.clear();
    this.auction = NO_AUCTION;
    this.mall = NO_MALL;
    this.selfName = "";
    this.authoritative = moveState(welcome.spawn_x_mm, welcome.spawn_y_mm);
    this.predictor = new Predictor(
      this.authoritative,
      this.options.stats,
      this.options.world,
    );
    this.swingLock = new SwingLock();
    this.swingFacing = null;

    this.setStatus("joined", `entity ${welcome.entity_id} on ${welcome.map_id}`);
    this.startTicking();
  }

  private onSnapshot(snapshot: Snapshot): void {
    const now = this.options.now();
    this.serverTick = snapshot.tick;
    this.ackSeq = snapshot.ack_seq;
    this.snapshotsApplied += 1;

    let sawSelf = false;
    let footprintsMoved = false;
    for (const delta of snapshot.entities) {
      if (this.observeFootprint(delta)) footprintsMoved = true;
      if (delta.id === this.entityId) {
        // The local player is reconciled, never interpolated: it is drawn from
        // prediction, which is ahead of this snapshot by the round trip.
        this.reconcile(delta);
        sawSelf = true;
        continue;
      }
      this.entities.observe(delta, now);
    }
    // `RecvItemGroundAddPacket` hands the vnum to `CreateItem`
    // (`PythonNetworkStreamPhaseGameItem.cpp:296-311`); the renderer reads it next frame.
    for (const begun of snapshot.skill_motions) {
      this.options.onSkillMotion?.(begun.entity_id, begun.motion, begun.grade);
    }
    for (const told of snapshot.ground_items) {
      this.groundItems.set(told.entity_id, { vnum: told.vnum, seenMs: performance.now() });
    }
    for (const id of snapshot.removed) {
      this.entities.remove(id);
      this.groundItems.delete(id);
      if (this.footprints.delete(id)) footprintsMoved = true;
    }
    if (footprintsMoved) this.restampObjectOverlay();

    if (!sawSelf) this.reconcileAckOnly();
  }

  private observeFootprint(delta: EntityDelta): boolean {
    const { footprint_x0_mm: x0, footprint_y0_mm: y0 } = delta;
    const { footprint_x1_mm: x1, footprint_y1_mm: y1 } = delta;
    if (x0 === null || y0 === null || x1 === null || y1 === null) return false;
    const previous = this.footprints.get(delta.id);
    if (
      previous !== undefined &&
      previous[0] === x0 &&
      previous[1] === y0 &&
      previous[2] === x1 &&
      previous[3] === y1
    ) {
      return false;
    }
    this.footprints.set(delta.id, [x0, y0, x1, y1]);
    return true;
  }

  private restampObjectOverlay(): void {
    const world = this.options.world;
    world.clearObjectAttrs();
    for (const [x0, y0, x1, y1] of this.footprints.values()) {
      world.stampObjectRect(x0, y0, x1, y1);
    }
    // `UpdateAttribute` places each standing building's own records at its
    // transform (`ActorInstanceData.cpp:20-37`), which a moving body is then
    // stopped on (`PythonBackground.cpp:571-597`).
    const table = this.options.guildAttributes?.() ?? null;
    const standing: StandingBuilding[] = [];
    for (const id of table === null ? [] : this.footprints.keys()) {
      const building = this.entities.get(id);
      if (building === null) continue;
      standing.push({ vnum: building.buildingVnum, xMm: building.xMm, yMm: building.yMm, heading: building.heading });
    }
    world.setBuildings(table === null || standing.length === 0 ? null : guildBuildingCollision(table, standing));
  }

  private reconcileAckOnly(): void {
    if (this.predictor === null || this.authoritative === null) return;
    this.predictor.reconcile(this.authoritative, this.ackSeq);
  }

  private reconcile(delta: EntityDelta): void {
    const predictor = this.predictor;
    if (predictor === null || this.authoritative === null) return;

    this.selfVitals = mergeVitals(this.selfVitals, delta);
    // Only a snapshot's own health counts. `Standing` carries a maximum and no
    // current, and trusting it here is what put the death screen on every join.
    if (delta.hp !== null) {
      // The first snapshot that reports a health is what unblocks the maximum
      // `Standing` sent earlier; without this the panel would wait for a
      // level-up to learn the number it was told on the way in.
      if (!this.sawHealth && this.standing.maxHp > 0) {
        this.selfVitals = { ...this.selfVitals, maxHp: this.standing.maxHp };
      }
      this.sawHealth = true;
    }
    // Sent once, on first appearance; absent afterwards, so never overwrite a
    // known name with the empty string a later delta omits.
    if (delta.name !== null && delta.name.length > 0) this.selfName = delta.name;
    // The same rule, for the same reason. Also sent once and never resent, so
    // an absent `race` on every later delta must not reset us to race 0 -- and
    // race 0 is a real body, so this cannot be spelled `if (delta.race)`.
    if (delta.race !== null) this.selfRace = delta.race;
    // `!== null` again, and here it is load-bearing rather than pedantic: zero
    // *is* the dismount, and the encoder sends it explicitly for that reason
    // (`snapshot.py`'s `_changed`). `if (delta.mount)` would keep the horse
    // under a character who has just got off it.
    if (delta.mount !== null) this.selfMount = delta.mount;
    // Zero is the removal frame. An absent field means unchanged, not clear.
    if (delta.flags !== null) this.selfFlags = delta.flags;
    // The same rule: zero is a demotion, absent is unchanged.
    if (delta.gm_level !== null) this.selfGmLevel = delta.gm_level;
    if (delta.affect_flags !== null) this.selfAffectFlags = delta.affect_flags;

    const previous = this.authoritative;
    this.authoritative = {
      xMm: delta.x_mm ?? previous.xMm,
      yMm: delta.y_mm ?? previous.yMm,
      heading: delta.heading ?? previous.heading,
      gait: toGait(delta.gait ?? previous.gait),
    };
    predictor.reconcile(this.authoritative, this.ackSeq);
  }

  // --------------------------------------------------------------- outbound

  private startTicking(): void {
    this.stopTimers();
    this.pacingMs = TICK_MS;
    this.clock = new InputClock(this.options.now(), TICK_MS);
    this.scheduleTick();
    this.pingTimer = setInterval(() => this.sendPing(), PING_INTERVAL_MS);
    this.sendPing();
  }

  private scheduleTick(): void {
    this.tickTimer = setTimeout(() => {
      this.pumpInputs();
      if (this.tickTimer !== null) this.scheduleTick();
    }, this.clock.delayMs(this.options.now()));
  }

  private repace(unacked: number): void {
    const drift = (unacked - TARGET_UNACKED) * PACING_GAIN_MS;
    const clamped = Math.max(-MAX_PACING_SKEW_MS, Math.min(MAX_PACING_SKEW_MS, drift));
    this.pacingMs = TICK_MS + clamped;
  }

  pumpInputs(): number {
    // Before the socket has said hello there is no clock worth honouring -- the
    // deadline is still the one from construction, and paying out that debt on
    // join would be a burst of walking nobody asked for.
    const predictor = this.predictor;
    if (this.tickTimer === null || predictor === null) return 0;

    this.repace(predictor.unacked);
    const owed = this.clock.due(this.options.now(), this.pacingMs);
    let produced = 0;
    while (produced < owed) {
      if (produced > 0 && predictor.unacked >= MAX_IN_FLIGHT) break;
      this.tick();
      produced += 1;
    }
    return produced;
  }

  /** Current input period in ms. Exposed for tests and the HUD. */
  get tickPacingMs(): number {
    return this.pacingMs;
  }

  /** One simulation tick: sample input, predict, send. Exposed for tests. */
  tick(): void {
    const predictor = this.predictor;
    if (predictor === null) return;

    // The held attack key, spent on the simulation clock rather than on the
    // render clock -- see `ConnectionOptions.onInputTick`. Before `sample()`
    // and before `swingLock.locked` is read, so a swing this tick arms roots
    // this tick's own input frame.
    this.options.onInputTick?.();

    const sampled = this.options.input.sample();
    const locked = this.swingLock.locked;
    this.swingLock.advance();
    const capped = this.walkModeOn && sampled.gait === Gait.RUN ? Gait.WALK : sampled.gait;
    const gait = this.dead || locked ? Gait.IDLE : capped;
    const facing = locked && this.swingFacing !== null
      ? this.swingFacing
      : sampled.heading;
    if (!locked) this.swingFacing = null;
    const move = predictor.pushInput(facing & HEADING_MASK, gait, locked);

    const input = new Input();
    input.seq = move.seq;
    input.heading = move.heading;
    input.gait = move.gait;
    this.send(input.encode());
  }

  armSwingLock(ticks?: number, nextSwingTicks?: number): void {
    if (this.predictor === null) return;
    this.swingLock.swing(
      ticks ?? cooldownTicksFor(this.standing.attackSpeed),
      nextSwingTicks,
    );
  }

  get swingDue(): boolean {
    return this.swingLock.dueForSwing;
  }

  faceTowards(heading: number): void {
    this.swingFacing = heading & HEADING_MASK;
  }


  attack(targetId: number, lockTicks?: number, nextSwingTicks?: number, comboStep = 0): void {
    if (this.predictor === null) return;
    this.armSwingLock(lockTicks, nextSwingTicks);
    const attack = new Attack();
    // The swing is stamped with the input frame it belongs to, so the server can
    // rewind the world to what this client legitimately saw when it swung.
    attack.seq = this.predictor.lastSeq;
    attack.target_id = targetId;
    attack.combo_step = comboStep;
    this.send(attack.encode());
  }

  say(text: string, channel: number = ChatChannel.SAY): void {
    if (text.length === 0) return;
    const chat = new Chat();
    chat.text = text;
    chat.channel = channel;
    this.send(chat.encode());
  }

  whisper(targetName: string, text: string): void {
    if (targetName.length === 0 || text.length === 0) return;
    const whisper = new Whisper();
    whisper.target_name = targetName;
    whisper.text = text;
    this.send(whisper.encode());
  }

  pickUp(entityId: number): void {
    if (this.predictor === null || this.dead) return;
    const pickup = new PickUp();
    pickup.entity_id = entityId;
    this.send(pickup.encode());
  }

  clickNpc(npcId: number): void {
    if (this.predictor === null || this.dead) return;
    const click = new ClickNpc();
    click.npc_id = npcId;
    this.send(click.encode());
  }

  questReply(token: number, choice: number): void {
    if (this.predictor === null) return;
    const reply = new QuestReply();
    reply.token = token;
    reply.choice = Math.max(0, Math.min(255, Math.floor(choice)));
    this.send(reply.encode());
  }

  /** Answer `input()` with the prompt token and the typed string only. */
  questInput(token: number, text: string): void {
    if (this.predictor === null) return;
    const answer = new QuestInputAnswer();
    answer.token = token;
    answer.text = text;
    this.send(answer.encode());
  }

  /** Answer one quest confirmation. No player id or timeout returns. */
  questConfirm(token: number, accept: boolean): void {
    if (this.predictor === null) return;
    const answer = new QuestConfirmAnswer();
    answer.token = token;
    answer.accept = accept;
    this.send(answer.encode());
  }

  /** Select one bag cell. No item identity or count returns. */
  questItem(token: number, cell: number): void {
    if (this.predictor === null) return;
    const answer = new QuestItemAnswer();
    answer.token = token;
    answer.cell = Math.max(0, Math.floor(cell));
    this.send(answer.encode());
  }

  questInfo(index: number, describe = false): void {
    if (this.predictor === null || this.dead) return;
    const info = new QuestInfo();
    info.index = index;
    info.describe = describe;
    this.send(info.encode());
  }

  /** Use a bag slot. */
  useItem(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const use = new UseItem();
    use.slot = slot;
    this.send(use.encode());
  }

  /** Put a slot back on the ground. Zero means the whole stack. */
  dropItem(slot: number, count = 0): void {
    if (this.predictor === null || this.dead) return;
    const drop = new DropItem();
    drop.slot = slot;
    drop.count = count;
    this.send(drop.encode());
  }

  dropGold(amount: number): void {
    if (this.predictor === null || this.dead) return;
    const drop = new DropGold();
    drop.amount = Math.max(0, Math.floor(amount));
    this.send(drop.encode());
  }

  moveItem(source: number, destination: number, count = 0): void {
    if (this.predictor === null || this.dead) return;
    const move = new MoveItem();
    move.slot = source;
    move.dest = destination;
    move.count = count;
    this.send(move.encode());
  }

  /** Wear a bag slot. Where it goes is the item's to say, not this client's. */
  equipItem(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const equip = new EquipItem();
    equip.slot = slot;
    this.send(equip.encode());
  }

  /** Take a worn piece off. */
  unequip(wear: number): void {
    if (this.predictor === null || this.dead) return;
    const off = new Unequip();
    off.wear = wear;
    this.send(off.encode());
  }

  sellItem(slot: number, count = 0): void {
    if (this.predictor === null || this.dead) return;
    const sell = new SellItem();
    sell.slot = slot;
    sell.count = count;
    this.send(sell.encode());
  }

  buyItem(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const buy = new BuyItem();
    buy.slot = slot;
    this.send(buy.encode());
  }

  /** Tell the server the counter is shut. Harmless if it already was. */
  closeShop(): void {
    if (this.predictor === null) return;
    this.send(new CloseShop().encode());
  }

  refineInfo(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new RefineInfo();
    ask.slot = Math.max(0, Math.min(255, Math.floor(slot)));
    this.send(ask.encode());
  }

  refine(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const attempt = new Refine();
    attempt.slot = Math.max(0, Math.min(255, Math.floor(slot)));
    this.send(attempt.encode());
  }

  /** Ask another player to trade. The answer is an `ExchangeState` for both of
   *  you, or a `Notice` for whoever asked. */
  exchangeStart(targetId: number): void {
    if (this.predictor === null || this.dead) return;
    const start = new ExchangeStart();
    start.target_id = targetId;
    this.send(start.encode());
  }

  /** Put one of this player's own bag slots in the window. Whole stack; there
   *  is no count, because an offer is keyed by the slot it came from. */
  exchangeOffer(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const offer = new ExchangeOffer();
    offer.slot = slot;
    this.send(offer.encode());
  }

  /** Take one back out, by the bag slot it was offered from. */
  exchangeWithdraw(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const withdraw = new ExchangeWithdraw();
    withdraw.slot = slot;
    this.send(withdraw.encode());
  }

  /** Put money in. Once only: the server refuses a second amount rather than
   *  replacing the first, so a panel offering to edit it would be offering
   *  something that cannot happen. */
  exchangeGold(amount: number): void {
    if (this.predictor === null || this.dead) return;
    const gold = new ExchangeGold();
    gold.amount = Math.max(0, Math.floor(amount));
    this.send(gold.encode());
  }

  exchangeAccept(token: number): void {
    if (this.predictor === null || this.dead) return;
    const accept = new ExchangeAccept();
    accept.token = token;
    this.send(accept.encode());
  }

  /** Walk away. Nothing was moved, so nothing is undone. */
  exchangeCancel(): void {
    if (this.predictor === null) return;
    this.send(new ExchangeCancel().encode());
  }

  /** Open this account's storeroom. The server resolves the nearby keeper;
   *  the password is checked server-side and this build accepts only `000000`,
   *  which is what the original accepts for an account with no stored row. */
  openSafebox(password: string): void {
    if (this.predictor === null || this.dead) return;
    const open = new OpenSafebox();
    open.password = password;
    this.send(open.encode());
  }

  /** Rent the first page at the keeper used to open the box. No price here:
   *  it is the server's, and it arrives on the `Safebox` frame. */
  safeboxUnlock(): void {
    if (this.predictor === null || this.dead) return;
    const unlock = new SafeboxUnlock();
    this.send(unlock.encode());
  }

  openGoldBarShop(): void {
    if (this.predictor === null || this.dead) return;
    this.send(new OpenGoldBarShop().encode());
  }

  /** Bag slot into a storeroom cell. The cell must be empty. */
  safeboxDeposit(slot: number, cell: number): void {
    if (this.predictor === null || this.dead) return;
    const put = new SafeboxDeposit();
    put.slot = slot;
    put.cell = cell;
    this.send(put.encode());
  }

  /** Storeroom cell into a bag slot. The slot must be empty -- the server does
   *  not go looking for another, and a withdraw that landed somewhere else is
   *  one the two sides can disagree about. */
  safeboxWithdraw(cell: number, slot: number): void {
    if (this.predictor === null || this.dead) return;
    const take = new SafeboxWithdraw();
    take.cell = cell;
    take.slot = slot;
    this.send(take.encode());
  }

  /** Rearrange inside the box. Zero count means the whole stack. */
  safeboxMove(source: number, destination: number, count = 0): void {
    if (this.predictor === null || this.dead) return;
    const move = new SafeboxMove();
    move.source = source;
    move.destination = destination;
    move.count = count;
    this.send(move.encode());
  }

  /** Tell the server the storeroom is shut. Load-bearing: an open box blocks a
   *  trade, so a panel that closed itself without saying would leave the player
   *  unable to trade for the rest of the session. */
  closeSafebox(): void {
    if (this.predictor === null) return;
    this.send(new CloseSafebox().encode());
  }

  /** Ask another player to group up. The answer is a `PartyInvitation` on their
   *  screen, or a `Notice` on this one. */
  createCharacter(
    slot: number,
    name: string,
    race: number,
    shape: number,
  ): void {
    const create = new CreateCharacter();
    create.slot = slot;
    create.name = name;
    create.race = race;
    create.shape = shape;
    this.send(create.encode());
  }

  /** Choose the account's first kingdom. The server persists and validates it. */
  selectEmpire(empire: number): void {
    const select = new SelectEmpire();
    select.empire = empire;
    this.send(select.encode());
  }

  selectCharacter(slot: number): void {
    const pick = new SelectCharacter();
    pick.slot = slot;
    this.send(pick.encode());
  }

  deleteCharacter(slot: number, confirm: string): void {
    const ask = new DeleteCharacter();
    ask.slot = slot;
    ask.confirm = confirm;
    this.send(ask.encode());
  }

  register(username: string, password: string): void {
    const ask = new Register();
    ask.username = username;
    ask.password = password;
    this.send(ask.encode());
  }

  partyInvite(targetId: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new PartyInvite();
    ask.target_id = targetId;
    this.send(ask.encode());
  }

  partyAnswer(token: number, accept: boolean): void {
    if (this.predictor === null || this.dead) return;
    const answer = new PartyAnswer();
    answer.token = token;
    answer.accept = accept;
    this.send(answer.encode());
  }

  emote(emote: number, targetId = 0): void {
    if (this.predictor === null || this.dead) return;
    const gesture = new Emote();
    gesture.emote = emote;
    gesture.target_id = targetId;
    this.send(gesture.encode());
  }

  emoteAnswer(token: number, accept: boolean): void {
    if (this.predictor === null || this.dead) return;
    const answer = new EmoteAnswer();
    answer.token = token;
    answer.accept = accept;
    this.send(answer.encode());
  }

  fish(heading: number): void {
    if (this.predictor === null || this.dead) return;
    const cast = new Fish();
    cast.heading = heading;
    this.send(cast.encode());
  }

  mine(heading: number): void {
    if (this.predictor === null || this.dead) return;
    const swing = new Mine();
    swing.heading = heading;
    this.send(swing.encode());
  }

  /** Answer only the server-issued question the panel drew: token plus O/X. */
  answerOX(token: number, answer: boolean): void {
    if (this.predictor === null || this.dead) return;
    const choice = new OXAnswer();
    choice.token = Math.max(0, Math.floor(token));
    choice.answer = answer;
    this.send(choice.encode());
  }

  recall(): void {
    if (this.predictor === null || !this.dead) return;
    this.send(new Recall().encode());
  }

  /** `/restart_here` (`cmd_general.cpp:635-641`): revive where he fell. */
  restartHere(): void {
    if (this.predictor === null || !this.dead) return;
    this.send(new RestartHere().encode());
  }

  useDungeonDoor(): void {
    if (this.predictor === null || this.dead) return;
    this.send(new UseDungeonDoor().encode());
  }

  /** Walk out, which for a leader dissolves the party. One message for both,
   *  because the server makes no distinction: a departure that would leave one
   *  member behind disbands it anyway. */
  partyLeave(): void {
    if (this.predictor === null) return;
    this.send(new PartyLeave().encode());
  }

  /** Throw out one row from the party frame that drew it. */
  partyKick(slot: number, rosterRevision = this.party.roster_revision): void {
    if (this.predictor === null || this.dead) return;
    const kick = new PartyKick();
    kick.roster_revision = rosterRevision;
    kick.slot = Math.max(0, Math.floor(slot));
    this.send(kick.encode());
  }

  /** Found a guild. Level, price and the name itself are all the server's to
   *  check; nothing local is spent and nothing local is drawn until a `Guild`
   *  frame comes back. */
  guildCreate(name: string): void {
    if (this.predictor === null || this.dead) return;
    const create = new GuildCreate();
    create.name = name;
    this.send(create.encode());
  }

  /** Ask a player into the guild this client is in. Which guild is read off the
   *  connection by the server, so there is nothing here to aim. */
  guildInvite(targetId: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new GuildInvite();
    ask.target_id = targetId;
    this.send(ask.encode());
  }

  guildAnswer(token: number, accept: boolean): void {
    if (this.predictor === null || this.dead) return;
    const answer = new GuildAnswer();
    answer.token = token;
    answer.accept = accept;
    this.send(answer.encode());
  }

  /** Walk out. Refused for a master, who disbands or hands the guild over --
   *  which is why this is not also the disband, unlike `partyLeave`. */
  guildLeave(): void {
    if (this.predictor === null) return;
    this.send(new GuildLeave().encode());
  }

  /** Dissolve the whole guild. Master only, and the server decides that. */
  guildDisband(): void {
    if (this.predictor === null) return;
    this.send(new GuildDisband().encode());
  }

  guildWarChallenge(token: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new GuildWarChallenge();
    ask.token = token;
    this.send(ask.encode());
  }

  /** Refuse one incoming declaration by its server-issued opaque token. */
  guildWarRefuse(token: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new GuildWarRefuse();
    ask.token = token;
    this.send(ask.encode());
  }

  /** Post one server-authored guild-board row; membership and notice authority
   * remain server decisions (`guild.cpp:1006-1017`). */
  guildPostComment(content: string): void {
    if (this.predictor === null || this.dead) return;
    const ask = new GuildPostComment();
    ask.content = content;
    this.send(ask.encode());
  }

  /** Delete one row from the server-owned board frame that drew it. */
  guildDeleteComment(
    slot: number,
    boardRevision = this.guildComments.board_revision,
  ): void {
    if (this.predictor === null || this.dead) return;
    const ask = new GuildDeleteComment();
    ask.board_revision = boardRevision;
    ask.slot = Math.max(0, Math.floor(slot));
    this.send(ask.encode());
  }

  /** Re-read the server-owned guild board without supplying any identity. */
  guildRefreshComments(): void {
    if (this.predictor === null || this.dead) return;
    this.send(new GuildRefreshComments().encode());
  }

  onGuildWarMenu(sink: (menu: GuildWarMenuFrame) => void): void {
    this.options.onGuildWarMenu = sink;
    sink(this.guildWarMenu);
  }

  onGuildWarArena(sink: (arena: GuildWarArenaFrame) => void): void {
    this.options.onGuildWarArena = sink;
    sink(this.guildWarArena);
  }

  onGuildComments(sink: (comments: GuildCommentsFrame) => void): void {
    this.options.onGuildComments = sink;
    sink(this.guildComments);
  }

  onGuildLand(sink: (state: GuildLandStateFrame) => void): void {
    this.options.onGuildLand = sink;
    sink(this.guildLand);
  }

  // ---- guild ranks, the bank and contribution

  guildRank(
    grade: number,
    name: string,
    mayInvite: boolean,
    mayKick: boolean,
    mayNotice: boolean,
    maySkill: boolean,
  ): void {
    if (this.predictor === null || this.dead) return;
    const edit = new GuildRank();
    edit.grade = grade;
    edit.name = name;
    edit.may_invite = mayInvite;
    edit.may_kick = mayKick;
    edit.may_notice = mayNotice;
    edit.may_skill = maySkill;
    this.send(edit.encode());
  }

  guildPromote(
    slot: number,
    grade: number,
    rosterRevision = this.guild.roster_revision,
  ): void {
    if (this.predictor === null || this.dead) return;
    const move = new GuildPromote();
    move.roster_revision = rosterRevision;
    move.slot = slot;
    move.grade = grade;
    this.send(move.encode());
  }

  guildExpel(slot: number, rosterRevision = this.guild.roster_revision): void {
    if (this.predictor === null || this.dead) return;
    const expel = new GuildExpel();
    expel.roster_revision = rosterRevision;
    expel.slot = slot;
    this.send(expel.encode());
  }

  /** Select or clear one row from the rendered guild roster. */
  guildGeneral(
    slot: number,
    assign: boolean,
    rosterRevision = this.guild.roster_revision,
  ): void {
    if (this.predictor === null || this.dead) return;
    const change = new GuildGeneral();
    change.roster_revision = rosterRevision;
    change.slot = slot;
    change.assign = assign;
    this.send(change.encode());
  }

  /** Make the member in one seat the master. Its own request rather than
   *  `guildPromote(slot, 1)`, for the reason `guildDisband` is not
   *  `guildLeave`: the sender cannot take it back afterwards. */
  guildHandOver(
    slot: number,
    rosterRevision = this.guild.roster_revision,
  ): void {
    if (this.predictor === null || this.dead) return;
    const give = new GuildHandOver();
    give.roster_revision = rosterRevision;
    give.slot = slot;
    this.send(give.encode());
  }

  /** Move gold between this player's purse and the guild's bank. The amount is
   *  clamped by the server, the recipient of a withdrawal is the guild's own
   *  master, and neither is nameable here. */
  guildBank(amount: number, withdraw: boolean): void {
    if (this.predictor === null || this.dead) return;
    const move = new GuildBank();
    move.amount = amount;
    move.withdraw = withdraw;
    this.send(move.encode());
  }

  /** Burn character experience to raise the guild's level. The amount is the
   *  *character* experience offered; what it buys is the server's arithmetic,
   *  a hundred to one (`guild.cpp:932-936`). */
  guildOffer(amount: number): void {
    if (this.predictor === null || this.dead) return;
    const offer = new GuildOffer();
    offer.amount = amount;
    this.send(offer.encode());
  }

  /** Restore shared Dragon Ghost. The amount is requested points; gold is
   *  `amount * 100` on the server (`input_main.cpp:2396`, `guild.cpp:1702-1725`)
   *  and cannot be named here. */
  guildChargePower(amount: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new GuildChargePower();
    ask.amount = amount;
    this.send(ask.encode());
  }

  /** Spend one server-granted guild point on Holy Armour. */
  guildSkillUp(skillVnum = 154): void {
    if (this.predictor === null || this.dead) return;
    const skill = new GuildSkillUp();
    skill.skill = Math.max(0, Math.min(255, Math.floor(skillVnum)));
    this.send(skill.encode());
  }

  /** Ask to use Holy Armour; cost, target, cooldown and recipients stay server-owned. */
  guildUseSkill(skillVnum = 154): void {
    if (this.predictor === null || this.dead) return;
    const skill = new GuildUseSkill();
    skill.skill = Math.max(0, Math.min(255, Math.floor(skillVnum)));
    this.send(skill.encode());
  }

  guildBuyLand(): void {
    if (this.predictor === null || this.dead) return;
    this.send(new GuildBuyLand().encode());
  }

  guildPlaceBuilding(vnum: number, x: number, y: number, rotation: number): void {
    if (this.predictor === null || this.dead) return;
    const request = new GuildPlaceBuilding();
    request.vnum = vnum; request.x = x; request.y = y; request.rotation = rotation;
    this.send(request.encode());
  }

  guildUpgradeBuilding(slot: number, landRevision = this.guildLand.revision): void {
    if (this.predictor === null || this.dead) return;
    const request = new GuildUpgradeBuilding();
    request.land_revision = landRevision;
    request.slot = Math.max(0, Math.floor(slot));
    this.send(request.encode());
  }

  /** Choose how the party divides what it earns: 0 by level weight, 1 evenly.
   *  Leader only, and the server says so. */
  partyExpMode(mode: number): void {
    if (this.predictor === null || this.dead) return;
    const chosen = new PartyExpMode();
    chosen.mode = Math.max(0, Math.min(255, Math.floor(mode)));
    this.send(chosen.encode());
  }

  partyAttacker(
    slot: number,
    assign: boolean,
    rosterRevision = this.party.roster_revision,
  ): void {
    if (this.predictor === null || this.dead) return;
    const request = new PartyAttacker();
    request.roster_revision = rosterRevision;
    request.slot = Math.max(0, Math.floor(slot));
    request.assign = assign;
    this.send(request.encode());
  }

  /** Choose a non-attacker role for one row in the frame the server sent. */
  partyRole(
    slot: number,
    role: number,
    assign: boolean,
    rosterRevision = this.party.roster_revision,
  ): void {
    if (this.predictor === null || this.dead) return;
    const request = new PartyRole();
    request.roster_revision = rosterRevision;
    request.slot = Math.max(0, Math.floor(slot));
    request.role = Math.max(0, Math.min(255, Math.floor(role)));
    request.assign = assign;
    this.send(request.encode());
  }

  partyHeal(): void {
    if (this.predictor === null || this.dead) return;
    const request = new PartyUseSkill();
    request.skill = PARTY_SKILL_HEAL;
    this.send(request.encode());
  }

  spendPoint(stat: number): void {
    if (this.predictor === null || this.dead) return;
    const request = new SpendPoint();
    request.stat = stat;
    this.send(request.encode());
  }

  /** Confirm the server-issued reset-scroll offer.  Empty by design: the
   * server re-reads the offered cell, level and existing sheet. */
  statusReset(): void {
    if (this.predictor === null || this.dead) return;
    this.send(new StatusReset().encode());
  }

  setPvpMode(mode: number): void {
    if (this.predictor === null) return;
    const request = new SetPvpMode();
    request.mode = mode;
    this.send(request.encode());
  }

  useSkill(vnum: number, targetId = 0): void {
    if (this.predictor === null || this.dead) return;
    const cast = new UseSkill();
    cast.seq = this.predictor.lastSeq;
    cast.skill = vnum;
    cast.target_id = targetId;
    this.send(cast.encode());
  }

  trainSkill(vnum: number): void {
    if (this.predictor === null || this.dead) return;
    const request = new SkillUp();
    request.skill = vnum;
    this.send(request.encode());
  }

  sendQuickSlotAdd(pos: number, type: number, target: number): void {
    if (this.predictor === null) return;
    const request = new QuickSlotAdd();
    request.pos = pos;
    request.type = type;
    request.target = target;
    this.send(request.encode());
  }

  /** Ask for a quick slot to be emptied. `CInputMain::QuickslotDelete` ->
   *  `CHARACTER::DelQuickslot` (`input_main.cpp:882-886`,
   *  `char_quickslot.cpp:100-114`). Its own verb rather than an add of type 0,
   *  which `SetQuickslot` refuses (`char_quickslot.cpp:82-83`). */
  sendQuickSlotDel(pos: number): void {
    if (this.predictor === null) return;
    const request = new QuickSlotDel();
    request.pos = pos;
    this.send(request.encode());
  }

  sendQuickSlotSwap(pos: number, posTo: number): void {
    if (this.predictor === null) return;
    const request = new QuickSlotSwap();
    request.pos = pos;
    request.pos_to = posTo;
    this.send(request.encode());
  }

  dismissPet(): void {
    if (this.predictor === null || this.dead) return;
    const ask = new PetCommand();
    ask.command = PET_DISMISS;
    this.send(ask.encode());
  }

  arenaChallenge(opponentId: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new ArenaChallenge();
    ask.opponent_id = opponentId;
    this.send(ask.encode());
  }

  arenaAnswer(token: number, accept: boolean): void {
    // Gated on `dead` like `partyAnswer`, so there is one rule about what a
    // corpse may send rather than a different one per panel. It is not the
    // defence: the server refuses a duel to a dead accepter regardless, and
    // has to, because this gate is a line in a file anybody can edit.
    if (this.predictor === null || this.dead) return;
    const answer = new ArenaAnswer();
    answer.token = token;
    answer.accept = accept;
    this.send(answer.encode());
  }

  arenaWatch(duelId: number): void {
    if (this.predictor === null || this.dead) return;
    const watch = new ArenaWatch();
    watch.duel_id = duelId;
    this.send(watch.encode());
  }

  openStall(
    sign: string,
    listings: readonly { sourceSlot: number; displayPos: number; price: number }[],
  ): void {
    if (this.predictor === null || this.dead) return;
    const ask = new OpenStall();
    ask.sign = sign;
    ask.listings = listings.map((line) => {
      const listing = new StallListing();
      listing.source_slot = line.sourceSlot;
      listing.display_pos = line.displayPos;
      listing.price = line.price;
      return listing;
    });
    this.send(ask.encode());
  }

  closeStall(): void {
    if (this.predictor === null) return;
    this.send(new CloseStall().encode());
  }

  enterStall(sellerId: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new EnterStall();
    ask.seller_id = sellerId;
    this.send(ask.encode());
  }

  /** Shut the stall window. Harmless if it already was. */
  leaveStall(): void {
    if (this.predictor === null) return;
    this.send(new LeaveStall().encode());
  }

  buyFromStall(slot: number): void {
    if (this.predictor === null || this.dead) return;
    const buy = new BuyFromStall();
    buy.slot = slot;
    this.send(buy.encode());
  }

  cubeInfo(): void {
    if (this.predictor === null || this.dead) return;
    this.send(new CubeInfo().encode());
  }

  cubeMake(slots: readonly number[]): void {
    if (this.predictor === null || this.dead) return;
    const build = new CubeMake();
    // Floored and clamped for the encoder's sake only. A duplicate or an
    // out-of-range cell is not filtered here: the server refuses both by name
    // (`crafting.Refusal.DUPLICATE_SLOT`, `NO_SUCH_SLOT`), and a client that
    // quietly tidied them up would hide a bug in its own tray from itself.
    build.slots = slots.map((slot) => Math.max(0, Math.min(65535, Math.floor(slot))));
    this.send(build.encode());
  }

  dragonSoulMove(source: number, dest: number): void {
    if (this.predictor === null || this.dead) return;
    const move = new DragonSoulMove();
    move.source = Math.max(0, Math.floor(source));
    move.dest = Math.max(0, Math.floor(dest));
    this.send(move.encode());
  }

  dragonSoulDeck(deck: number): void {
    if (this.predictor === null || this.dead) return;
    const pick = new DragonSoulDeck();
    pick.deck = Math.max(0, Math.min(255, Math.floor(deck)));
    this.send(pick.encode());
  }

  dragonSoulRefine(
    operation: number,
    souls: readonly number[],
    stones: readonly number[],
  ): void {
    if (this.predictor === null || this.dead) return;
    const ask = new DragonSoulRefine();
    ask.operation = Math.max(0, Math.min(255, Math.floor(operation)));
    // Not de-duplicated here: the server refuses a repeated cell by name, and a
    // client that quietly tidied one away would hide a bug in its own grid.
    ask.souls = souls.map((c) => Math.max(0, Math.floor(c)));
    ask.stones = stones.map((c) => Math.max(0, Math.floor(c)));
    this.send(ask.encode());
  }

  setTarget(id: number): void {
    if (this.predictor === null) return;
    const target = new Target();
    target.target = Math.max(0, Math.trunc(id));
    this.send(target.encode());
  }

  /** Point the bow, once per shot, immediately before the swing that looses it.
   *  Where the original sends it: the bow animation's own fly event calls
   *  `SendFlyTargetingPacket` and the shoot packet follows
   *  (`PythonPlayerEventHandler.cpp:232-244`). The point is only ever read by
   *  the server when `id` is 0 -- see the frame's own contract -- so a client
   *  that got it wrong moves a picture, not a hit. */
  flyTarget(id: number, xMm: number, yMm: number): void {
    if (this.predictor === null || this.dead) return;
    const aim = new FlyTarget();
    aim.target_id = Math.max(0, Math.trunc(id));
    aim.x_mm = Math.trunc(xMm);
    aim.y_mm = Math.trunc(yMm);
    this.send(aim.encode());
  }

  proposeMarriage(targetId: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new ProposeMarriage();
    ask.target_id = targetId;
    this.send(ask.encode());
  }

  answerMarriage(token: number, accept: boolean): void {
    if (this.predictor === null || this.dead) return;
    const answer = new AnswerMarriage();
    answer.token = token;
    answer.accept = accept;
    this.send(answer.encode());
  }

  marriageCommand(command: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new MarriageCommand();
    ask.command = command;
    this.send(ask.encode());
  }

  addFriend(name: string): void {
    if (this.predictor === null || this.dead) return;
    const ask = new AddFriend();
    ask.name = name;
    this.send(ask.encode());
  }

  /** Accept or decline one friend request, by the token that was drawn.
   *  `answerMarriage`'s rule; the stake is lower and the shape is identical. */
  answerFriend(token: number, accept: boolean): void {
    if (this.predictor === null || this.dead) return;
    const answer = new AnswerFriend();
    answer.token = token;
    answer.accept = accept;
    this.send(answer.encode());
  }

  /** Take somebody off this player's own list. **One direction** -- they keep
   *  you on theirs (`messenger_manager.cpp:219-236`). Not gated on `dead`, like
   *  `closeStall`: nothing about a corpse makes a list worth freezing. */
  removeFriend(name: string): void {
    if (this.predictor === null) return;
    const ask = new RemoveFriend();
    ask.name = name;
    this.send(ask.encode());
  }

  setBlockMode(
    whisper: boolean,
    messengerInvite: boolean,
    exchange: boolean,
    partyInvite: boolean,
    guildInvite: boolean,
    partyRequest = false,
  ): void {
    if (this.predictor === null) return;
    const ask = new SetBlockMode();
    ask.whisper = whisper;
    ask.messenger_invite = messengerInvite;
    ask.exchange = exchange;
    ask.party_invite = partyInvite;
    ask.guild_invite = guildInvite;
    ask.party_request = partyRequest;
    this.send(ask.encode());
  }

  uploadEmblem(bgra: readonly number[]): void {
    if (this.predictor === null || this.dead) return;
    const upload = new UploadEmblem();
    upload.bgra = bgra.map((sample) => Math.max(0, Math.min(255, Math.floor(sample))));
    this.send(upload.encode());
  }

  /** Send this player's guild symbol file, `GUILD_SYMBOL_CHUNK_BYTES` at a time
   *  and in order: 2014 sends it whole behind one header
   *  (`CGuildMarkUploader::__SendSymbolPacket`, `GuildMarkUploader.cpp:310-325`),
   *  which a bounded client frame cannot carry. */
  uploadGuildSymbol(file: Uint8Array): void {
    if (this.predictor === null || this.dead || file.length === 0) return;
    for (let offset = 0; offset < file.length; offset += GUILD_SYMBOL_CHUNK_BYTES) {
      const piece = new GuildSymbolChunk();
      piece.total = file.length;
      piece.offset = offset;
      piece.data = [...file.subarray(offset, offset + GUILD_SYMBOL_CHUNK_BYTES)];
      this.send(piece.encode());
    }
  }

  /** Name the copy of one guild's symbol this client holds; zero and zero for
   *  none (`__SendSymbolCRCList`, `GuildMarkDownloader.cpp:385-400`). */
  askGuildSymbol(guildId: number, crc: number, size: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new GuildSymbolCRC();
    ask.guild_id = Math.max(0, Math.floor(guildId));
    ask.crc = crc >>> 0;
    ask.size = Math.max(0, Math.floor(size));
    this.send(ask.encode());
  }

  /** Ask for one guild's emblem. Public information -- see `RequestEmblem`. */
  requestEmblem(guildId: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new RequestEmblem();
    ask.guild_id = Math.max(0, Math.floor(guildId));
    this.send(ask.encode());
  }

  openAuction(): void {
    if (this.predictor === null || this.dead) return;
    this.send(new OpenAuction().encode());
  }

  listLot(sourceSlot: number, startingBid: number, buyNowPrice: number): void {
    if (this.predictor === null || this.dead) return;
    const ask = new ListLot();
    ask.source_slot = sourceSlot;
    ask.starting_bid = startingBid;
    ask.buy_now_price = buyNowPrice;
    this.send(ask.encode());
  }

  placeBid(slot: number, total: number, boardRevision = this.auction.revision): void {
    if (this.predictor === null || this.dead) return;
    const bid = new PlaceBid();
    bid.board_revision = boardRevision;
    bid.slot = Math.max(0, Math.floor(slot));
    bid.total = total;
    this.send(bid.encode());
  }

  /** End a lot outright at the seller's fixed price. A lot id and no price, for
   *  `buyFromStall`'s reason: the number is another player's choice and the
   *  server holds it. */
  buyLotNow(slot: number, boardRevision = this.auction.revision): void {
    if (this.predictor === null || this.dead) return;
    const buy = new BuyLotNow();
    buy.board_revision = boardRevision;
    buy.slot = Math.max(0, Math.floor(slot));
    this.send(buy.encode());
  }

  claimLot(slot: number, boardRevision = this.auction.revision): void {
    if (this.predictor === null || this.dead) return;
    const claim = new ClaimLot();
    claim.board_revision = boardRevision;
    claim.slot = Math.max(0, Math.floor(slot));
    this.send(claim.encode());
  }

  mallCheckout(cell: number, slot: number): void {
    if (this.predictor === null || this.dead) return;
    const take = new MallCheckout();
    take.cell = cell;
    take.slot = slot;
    this.send(take.encode());
  }

  useItemToItem(slot: number, dest: number): void {
    if (this.predictor === null || this.dead) return;
    const use = new UseItemToItem();
    use.slot = slot;
    use.dest = dest;
    this.send(use.encode());
  }

  useItemOnNpc(slot: number, npcId: number): void {
    if (this.predictor === null || this.dead) return;
    const use = new UseItemToNpc();
    use.slot = slot;
    use.npc_id = npcId;
    this.send(use.encode());
  }

  private sendPing(): void {
    const ping = new Ping();
    this.pingSentAtMs = this.elapsed();
    this.send(ping.encode());
  }

  /** Milliseconds since this connection object was made; the wire carries an
   *  unsigned varint, so an epoch timestamp would cost six bytes for nothing. */
  private elapsed(): number {
    return Math.max(0, Math.round(this.options.now() - this.startedAtMs));
  }
}
